import { getAssetLabel } from 'src/data/asset';
import { ASSET_TYPE, type AssetType } from 'src/data/asset/const';
import { BRIEF_TYPE, type BriefType } from 'src/data/brief/const';
import {
  alignChannelWithBackend,
  type BackendChannel,
  CHANNEL,
  type Channel,
} from 'src/data/channel';
import { AI_SUGGESTION_MODE, type AiSuggestionMode } from 'src/data/generation';
import { EMAILS_CHANNELS_AND_RESOURCES } from 'src/data/performance';
import { TEMPLATE_OUTPUT_TYPE, type TemplateOutputType } from 'src/data/template';
import { UniqMap } from 'src/lib/UniqMap';

import { GENERATION_TOOL, type GenerationTool } from './const';
import { isDataDrivenGenerationTool, isTextGenerationTool } from './utils';

interface GenerationToolProps {
  title: string;
  contentType: string;
  briefType: BriefType;
  defaultAssetType: AssetType;
  channel?: Channel;
  copy?: string;
  description?: string;
  longDesc?: string;
  titleForTooltip?: string;
  defaultNumberOfVariations?: number;
  numberOfVariations?: number[];
  maxSuggestKeywords?: number;
  maxWordsPerKeyword?: number;
  maxCampaignKeywords?: number;

  // features support and other flags
  supportsSuggestKeywords?: boolean;
  supportsCustomerPersona?: boolean;
  supportsSubTemplates?: boolean;
  supportsPromptInstructions?: boolean;
  supportsBackgroundUrlBriefExtraction?: boolean;
  supportsCustomModel?: boolean;
  supportsCallToAction?: boolean;
  supportsBenchmark?: boolean;
  supportsCampaignKeywords?: boolean;
  supportsSectionHeader?: boolean;
  supportsSectionStyle?: boolean;
  supportsCsvExport?: boolean;
  supportsImage?: boolean;
  disableTOVModes?: boolean;
  disableFormula?: boolean;
  disableBrandVocabulary?: boolean;
  collapseAdvancedOptions?: boolean;
  generatingLoaderText?: string;
  basicHasMultipleGenerationModes?: boolean;
  excludedFromSuggestedPersonas?: boolean;
  variationLabelWithoutDescription?: boolean;
  noScore?: boolean;
  isPlural?: boolean;
  hidden?: boolean;
  showDescriptionInList?: boolean;

  // mixture tool specific
  mixtureSubAssetTypeSubtitles?: Record<string, string>;
  mixtureSubGenerationTools?: GenerationTool[];
  mixtureSubGenerationToolsModeOverride?: Record<string, AiSuggestionMode>;
  classifiedMixtureSubGenerationTools?: GenerationTool[];

  // multi-prompt tool specific
  multiPrompt?: boolean;
  promptFields?: PromptFields;
  promptTools?: PromptTools;
}

interface PromptFields {
  templateOutputType: TemplateOutputType;
  titles: Record<string, string>;
  placeholders: Record<string, string>;
  order: string[];
}

type PromptTools = Record<string, GenerationTool>;

const GENERATION_TOOL_PROPS = new UniqMap<GenerationTool, GenerationToolProps>();

/**
 * "basic" tools
 */
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.basicContentImprover, {
  title: 'Content Improver',
  contentType: 'Content Improver',
  copy: 'Spruce up your content. Make it more readable and engaging.',
  briefType: BRIEF_TYPE.canvas,
  defaultAssetType: ASSET_TYPE.canvasContentImprover,
  basicHasMultipleGenerationModes: true,
  supportsCustomerPersona: false,
  excludedFromSuggestedPersonas: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.basicEli5, {
  title: 'Explain it to a 5th Grader',
  contentType: 'Explain it to a 5th Grader',
  copy: 'Good writing is immediately understood. Make things simple with this tool so even a child can understand.',
  briefType: BRIEF_TYPE.canvas,
  defaultAssetType: ASSET_TYPE.canvasEli5,
  generatingLoaderText: "using 'Explain it to a 5th Grader'",
  disableTOVModes: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.basicSentenceExpander, {
  title: 'Sentence Expander',
  contentType: 'Sentence Expander',
  copy: 'Articulate your idea with this awesome tool.',
  briefType: BRIEF_TYPE.canvas,
  defaultAssetType: ASSET_TYPE.canvasSentenceExpander,
  generatingLoaderText: "using 'Sentence Expander'",
  disableTOVModes: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.basicAida, {
  title: 'AIDA',
  contentType: 'AIDA',
  copy: "Attention, interest, desire, action. It's the classic copywriting formula, studied and used by almost every copywriter on the planet.",
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.canvasAida,
  generatingLoaderText: "using 'AIDA'",
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.basicPas, {
  title: 'PAS',
  contentType: 'PAS',
  copy:
    'This formula gives your writing a simple chronology:\n' +
    'P = Introduce the problem.\n' +
    'A = Agitate the problem.\n' +
    'S = Solve the problem with your product or service.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.canvasPas,
  generatingLoaderText: "using 'PAS'",
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.basicBab, {
  title: 'BAB',
  contentType: 'BAB',
  copy: 'A three-step copywriting formula that presents the problem situation (before), the solution situation (after), and the solution itself (bridge).',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.canvasBab,
  generatingLoaderText: "using 'BAB'",
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.basicPerfectHeadline, {
  title: 'Perfect Product Headline',
  contentType: 'Perfect Product Headline',
  copy:
    'Do you waste endless hours looking through headlines?\n' +
    'Meet the Anyword Headline Generator — AI that writes perfect headlines for you.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.canvasPerfectHeadline,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.basicPersuasiveBulletPoint, {
  title: 'Powerful Bullet Points',
  contentType: 'Powerful Bullet Points',
  copy: "It's easy to make your product stand out with these powerful bullet points.",
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.canvasPersuasiveBulletPoint,
  disableTOVModes: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.basicFeatureToBenefitsPractical, {
  title: 'Features to Benefits',
  contentType: 'Features to Benefits',
  copy: 'The features of your product may not be enough to convince a customer to buy it. If you want to increase your sales, you need to start promoting the benefits of your product.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.canvasFeatureToBenefitsPractical,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 3,
  maxWordsPerKeyword: 4,
  generatingLoaderText: "using 'Features to Benefits'",
  disableTOVModes: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.basicValueProposition, {
  title: 'Value Proposition',
  contentType: 'Value Proposition',
  copy: 'What makes your product so great? How does your product help customers? Which customers would your product be perfect for?',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.canvasValueProposition,
  disableTOVModes: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.basicProductDescriptionList, {
  title: 'Product Description',
  contentType: 'Product Description',
  description: 'List',
  copy: 'Describe and explain your product in list form.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.canvasProductDescriptionList,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 5,
  maxWordsPerKeyword: 4,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.basicProductDescriptionRelaxed, {
  title: 'Product Description',
  contentType: 'Product Description',
  description: 'Paragraph',
  copy: 'Make your product description better with a click of a button.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.canvasProductDescriptionRelaxed,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 5,
  maxWordsPerKeyword: 4,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.basicBlogIdeas, {
  title: 'Blog Ideas',
  contentType: 'Blog Ideas',
  copy: 'Keep your blogs posts fresh and different every time.',
  briefType: BRIEF_TYPE.canvas,
  defaultAssetType: ASSET_TYPE.canvasBlogIdeas,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.basicVideoIdeas, {
  title: 'Video Topic Ideas',
  contentType: 'Video Topic Ideas',
  copy: "Have you been searching for ideas on how to make your videos more exciting? Well this might be the answer. It's guaranteed to make you smile.",
  briefType: BRIEF_TYPE.canvas,
  defaultAssetType: ASSET_TYPE.canvasVideoIdeas,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.basicTalkingPointsToParagraph, {
  title: 'Talking Points to Paragraph',
  contentType: 'Talking Points to Paragraph',
  copy: "Turn talking points into an engaging paragraph that's perfect for marketing materials.",
  briefType: BRIEF_TYPE.canvas,
  defaultAssetType: ASSET_TYPE.editorParagraph,
  generatingLoaderText: "using 'Talking Points to Paragraph'",
  disableTOVModes: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.basicProductReview, {
  title: 'Product Review',
  contentType: 'Product Review',
  copy: "Need to write a review for a product? Just give us the key points-we'll take care of the rest.",
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.productReview,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
  maxWordsPerKeyword: 4,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.summarizer, {
  title: 'Summarizer',
  contentType: 'Summarizer',
  copy: 'When you have a lot to say, but not enough time to say it, try using Summarizer!',
  briefType: BRIEF_TYPE.canvas,
  defaultAssetType: ASSET_TYPE.freeTextBody,
  basicHasMultipleGenerationModes: true,
  defaultNumberOfVariations: 1,
  disableTOVModes: true,
});

/**
 * "basic DD" tools
 */
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.basicFacebookAd, {
  title: 'Facebook Ad',
  contentType: 'Facebook Ad',
  copy: 'Generate exceptionally effective copy for your Facebook Ads.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.canvasFacebookAd,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.basicGoogleAd, {
  title: 'Google Ad',
  contentType: 'Google Ad',
  copy: 'Create high-converting descriptions and headlines for your Google Search Ads.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.canvasGoogleAd,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.basicTwitterAd, {
  title: 'X Ad',
  contentType: 'X Ad',
  copy: 'Write scroll-stopping copy for your X ads.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.canvasTwitterAd,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.basicLinkedinTextAd, {
  title: 'LinkedIn Ad',
  contentType: 'LinkedIn Ad',
  copy: 'Generate LinkedIn Text Ads that stand out and convert.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.canvasLinkedinTextAd,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.basicPinterestAd, {
  title: 'Pinterest Ad',
  contentType: 'Pinterest Ad',
  copy: 'Step up your Pinterest game with stellar descriptions and titles.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.canvasPinterestAd,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.basicLandingPage, {
  title: 'Webpage',
  contentType: 'Webpage',
  copy: 'Transform a product description into an entire landing page with this powerful tool.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.canvasLandingPage,
  defaultNumberOfVariations: 1,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.basicLandingPageSeo, {
  title: 'SEO Meta Description',
  contentType: 'SEO Meta Description',
  copy: 'Generate click-worthy meta descriptions to increase your visibility on Google.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.canvasLandingPageSeo,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.basicInstagramSocial, {
  title: 'Instagram Caption',
  contentType: 'Instagram Caption',
  copy: 'Generate catchy captions for your Instagram posts.',
  briefType: BRIEF_TYPE.socialManual,
  defaultAssetType: ASSET_TYPE.canvasInstagramSocial,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.basicFacebookSocial, {
  title: 'Facebook Post',
  contentType: 'Facebook Post',
  copy: 'Create engaging copy for your Facebook posts to maximize organic reach.',
  briefType: BRIEF_TYPE.socialManual,
  defaultAssetType: ASSET_TYPE.canvasFacebookSocial,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.basicTwitterSocial, {
  title: 'X Post',
  contentType: 'X Post',
  copy: 'Generate witty tweets to keep your followers engaged.',
  briefType: BRIEF_TYPE.socialManual,
  defaultAssetType: ASSET_TYPE.canvasTwitterSocial,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.basicLinkedinSocial, {
  title: 'LinkedIn Post',
  contentType: 'LinkedIn Post',
  copy: 'Cut through the noise by generating compelling copy for your LinkedIn posts.',
  briefType: BRIEF_TYPE.socialManual,
  defaultAssetType: ASSET_TYPE.canvasLinkedinSocial,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.basicYoutubeSocial, {
  title: 'YouTube Description',
  contentType: 'YouTube Description',
  copy: 'Write smart YouTube titles and descriptions to help viewers find your videos.',
  briefType: BRIEF_TYPE.socialManual,
  defaultAssetType: ASSET_TYPE.canvasYoutubeSocial,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.basicPinterestPinSocial, {
  title: 'Pinterest Pin',
  contentType: 'Pinterest Pin',
  copy: 'Step up your Pinterest game with stellar descriptions and titles.',
  briefType: BRIEF_TYPE.socialManual,
  defaultAssetType: ASSET_TYPE.canvasPinterestPinSocial,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.basicColdEmail, {
  title: 'Cold Email',
  contentType: 'Cold Email',
  copy: 'Create powerful, convincing emails to attract potential customers to your product or service.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.canvasColdEmail,
  defaultNumberOfVariations: 1,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.basicPromotionalEmail, {
  title: 'Promotional Email',
  contentType: 'Promotional Email',
  copy: 'Write an awesome email about a new product or an exciting promotion.',
  briefType: BRIEF_TYPE.unifiedPromotionalEmail,
  defaultAssetType: ASSET_TYPE.canvasPromotionalEmail,
  defaultNumberOfVariations: 1,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.basicEmailSubjectLine, {
  title: 'Email',
  contentType: 'Email',
  copy: 'Write enticing subject lines for all types of emails.',
  briefType: BRIEF_TYPE.email,
  defaultAssetType: ASSET_TYPE.canvasEmailSubjectLine,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.basicSms, {
  title: 'Promotional SMS',
  contentType: 'Promotional SMS',
  copy: 'Generate text messages that actually sell.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.canvasSms,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.generalPrompt, {
  title: 'Prompt',
  contentType: 'Prompt',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.generalProductDescription,
  supportsPromptInstructions: true,
  supportsSuggestKeywords: true,
  supportsCustomerPersona: true,
});

/**
 * "product" data-driven tools
 */
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.facebookPrimaryTextProduct, {
  title: 'Meta Ad',
  contentType: 'Meta Ad',
  description: 'Primary Text',
  copy: "Easily craft highly engaging copy for your Meta Ads' primary text.",
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.facebookPrimaryText,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 3,
  maxWordsPerKeyword: 4,
  supportsCustomerPersona: true,
  supportsCustomModel: true,
  channel: CHANNEL.facebook,
  collapseAdvancedOptions: true,
  supportsCallToAction: false,
  supportsBenchmark: true,
  showDescriptionInList: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.facebookHeadlineProduct, {
  title: 'Meta Ad',
  contentType: 'Meta Ad',
  description: 'Headline',
  copy: 'Generate exceptionally effective headlines for your Meta Ads.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.facebookHeadline,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsCustomerPersona: true,
  supportsCustomModel: true,
  channel: CHANNEL.facebook,
  collapseAdvancedOptions: true,
  supportsCallToAction: false,
  supportsBenchmark: true,
  showDescriptionInList: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.googleShortDescriptionProduct, {
  title: 'Google Search Ad',
  contentType: 'Google Search Ad',
  description: 'Short Description',
  copy: 'Create high-converting descriptions for your Google Search Ads.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.googleShortDescription,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsCallToAction: false,
  supportsBenchmark: true,
  supportsPromptInstructions: true,
  supportsBackgroundUrlBriefExtraction: true,
  channel: CHANNEL.google,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.googleDescriptionProduct, {
  title: 'Google Ad',
  contentType: 'Google Ad',
  description: 'Description',
  copy: 'Create high-converting descriptions for your Google Search Ads.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.googleDescription,
  maxSuggestKeywords: 2,
  channel: CHANNEL.google,
  maxCampaignKeywords: 3,
  supportsCampaignKeywords: true,
  disableTOVModes: false,
  maxWordsPerKeyword: 5,
  collapseAdvancedOptions: true,
  supportsCallToAction: false,
  supportsBenchmark: true,
  supportsPromptInstructions: true,
  supportsBackgroundUrlBriefExtraction: true,
  showDescriptionInList: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.googleHeadlineProduct, {
  title: 'Google Ad',
  contentType: 'Google Ad',
  description: 'Headline',
  copy: 'Make your Google Ads stand out with engaging headlines.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.googleHeadline,
  maxSuggestKeywords: 1,
  maxWordsPerKeyword: 5,
  channel: CHANNEL.google,
  maxCampaignKeywords: 3,
  supportsCampaignKeywords: true,
  disableTOVModes: true,
  collapseAdvancedOptions: true,
  supportsCallToAction: false,
  supportsBenchmark: true,
  supportsPromptInstructions: true,
  supportsBackgroundUrlBriefExtraction: true,
  showDescriptionInList: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.googleMediumHeadlineProduct, {
  title: 'Google Demand Gen Ad',
  contentType: 'Google Demand Gen Ad',
  description: 'Headline',
  copy: 'Make your Google Ads stand out with engaging headlines.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.googleMediumHeadline,
  maxSuggestKeywords: 1,
  maxWordsPerKeyword: 5,
  channel: CHANNEL.google,
  maxCampaignKeywords: 3,
  supportsCampaignKeywords: true,
  disableTOVModes: true,
  collapseAdvancedOptions: true,
  supportsCallToAction: false,
  supportsBenchmark: true,
  supportsPromptInstructions: true,
  supportsBackgroundUrlBriefExtraction: true,
  showDescriptionInList: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.googleShoppingAdTitle, {
  title: 'Google Shopping Ad',
  contentType: 'Google Shopping Ad',
  description: 'Headline',
  copy: 'Make your Google Ads stand out with engaging headlines.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.googleShoppingTitle,
  maxSuggestKeywords: 1,
  maxWordsPerKeyword: 5,
  channel: CHANNEL.google,
  maxCampaignKeywords: 3,
  supportsCampaignKeywords: true,
  disableTOVModes: true,
  collapseAdvancedOptions: true,
  supportsCallToAction: false,
  supportsPromptInstructions: true,
  supportsBackgroundUrlBriefExtraction: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.googleShoppingAdDescription, {
  title: 'Google Shopping Ad',
  contentType: 'Google Shopping Ad',
  description: 'Description',
  copy: 'Make your Google Ads stand out with engaging descriptions.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.googleShoppingDescription,
  maxSuggestKeywords: 1,
  maxWordsPerKeyword: 5,
  channel: CHANNEL.google,
  maxCampaignKeywords: 3,
  supportsCampaignKeywords: true,
  disableTOVModes: true,
  collapseAdvancedOptions: true,
  supportsCallToAction: false,
  supportsPromptInstructions: true,
  supportsBackgroundUrlBriefExtraction: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.googleLongHeadlineProduct, {
  title: 'Google Ad',
  contentType: 'Google Ad',
  description: 'Long Headline',
  copy: 'Make your Google Ads stand out with engaging headlines.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.googleLongHeadline,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsCallToAction: false,
  supportsBenchmark: true,
  supportsPromptInstructions: true,
  supportsBackgroundUrlBriefExtraction: true,
  channel: CHANNEL.google,
  showDescriptionInList: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.outbrainHeadlineProduct, {
  title: 'Outbrain Ad',
  contentType: 'Outbrain Ad',
  description: 'Title',
  copy: 'Generate irresistible Outbrain headlines that drive traffic.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.outbrainHeadline,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsCallToAction: false,
  supportsPromptInstructions: true,
  supportsBackgroundUrlBriefExtraction: true,
  showDescriptionInList: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.outbrainDescriptionProduct, {
  title: 'Outbrain Ad',
  contentType: 'Outbrain Ad',
  description: 'Description',
  copy: 'Generate irresistible Outbrain descriptions that drive traffic.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.outbrainDescription,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsCallToAction: false,
  supportsPromptInstructions: true,
  supportsBackgroundUrlBriefExtraction: true,
  showDescriptionInList: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.taboolaHeadlineProduct, {
  title: 'Taboola Ad',
  contentType: 'Taboola Ad',
  description: 'Title',
  copy: 'Generate irresistible Taboola titles that drive traffic.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.taboolaHeadline,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsCallToAction: false,
  supportsPromptInstructions: true,
  supportsBackgroundUrlBriefExtraction: true,
  showDescriptionInList: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.taboolaDescriptionProduct, {
  title: 'Taboola Ad',
  contentType: 'Taboola Ad',
  description: 'Description',
  copy: 'Generate irresistible Taboola descriptions that drive traffic.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.taboolaDescription,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsCallToAction: false,
  supportsPromptInstructions: true,
  supportsBackgroundUrlBriefExtraction: true,
  showDescriptionInList: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.twitterCopyProduct, {
  title: 'X Ad',
  contentType: 'X Ad',
  description: 'Copy',
  copy: 'Write scroll-stopping copy for your X Ads.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.twitterCopy,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 3,
  maxWordsPerKeyword: 4,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsCallToAction: false,
  supportsBackgroundUrlBriefExtraction: true,
  showDescriptionInList: true,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.imageWithDescription, {
  title: 'Images',
  contentType: 'Images',
  copy: '(deprecated)',
  briefType: BRIEF_TYPE.socialManual,
  defaultAssetType: ASSET_TYPE.imageWithDescription,
  defaultNumberOfVariations: 1,
  maxSuggestKeywords: 3,
  maxWordsPerKeyword: 4,
  supportsCustomerPersona: false,
  supportsPromptInstructions: true,
  supportsBackgroundUrlBriefExtraction: true,
  collapseAdvancedOptions: false,
  supportsSuggestKeywords: false,
  supportsBenchmark: false,
  supportsCustomModel: false,
  disableTOVModes: true,
  disableFormula: true,
  disableBrandVocabulary: true,
  noScore: true,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.imageByStyle, {
  title: 'Image',
  contentType: 'Image',
  copy: 'Turn your prompts or images into captivating visuals.',
  briefType: BRIEF_TYPE.canvas,
  defaultAssetType: ASSET_TYPE.image,
  defaultNumberOfVariations: 1,
  supportsCustomerPersona: true,
  supportsPromptInstructions: true,
  supportsBackgroundUrlBriefExtraction: true,
  disableTOVModes: true,
  disableFormula: true,
  disableBrandVocabulary: true,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.imageImprove, {
  title: 'Image',
  contentType: 'Image',
  briefType: BRIEF_TYPE.canvas,
  defaultAssetType: ASSET_TYPE.image,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.imageScorer, {
  title: 'Image',
  contentType: 'Image',
  briefType: BRIEF_TYPE.canvas,
  defaultAssetType: ASSET_TYPE.image,
  hidden: true,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.twitterTitleProduct, {
  title: 'X Ad',
  contentType: 'X Ad',
  description: 'Headline',
  copy: 'Create scroll-stopping X Ad headlines.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.twitterTitle,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsCallToAction: false,
  supportsBackgroundUrlBriefExtraction: true,
  showDescriptionInList: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.pinterestDescriptionProduct, {
  title: 'Pinterest Ad',
  contentType: 'Pinterest Ad',
  description: 'Description',
  copy: 'Take it one step further with dazzling Pinterest descriptions.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.pinterestDescription,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 3,
  maxWordsPerKeyword: 4,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsCallToAction: false,
  supportsBackgroundUrlBriefExtraction: true,
  showDescriptionInList: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.pinterestTitleProduct, {
  title: 'Pinterest Ad',
  contentType: 'Pinterest Ad',
  description: 'Title',
  copy: 'Step up your Pinterest game with stellar titles.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.pinterestTitle,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsCallToAction: false,
  supportsBackgroundUrlBriefExtraction: true,
  showDescriptionInList: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.linkedinHeadlineSingleImage, {
  title: 'LinkedIn Ad',
  contentType: 'LinkedIn Ad',
  description: 'Headline',
  copy: 'Stand out with a compelling headline for your LinkedIn Single Image Ad.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.linkedinSingleImageHeadline,
  supportsCustomModel: true,
  channel: CHANNEL.linkedin,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsCallToAction: false,
  supportsBenchmark: true,
  supportsBackgroundUrlBriefExtraction: true,
  showDescriptionInList: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.linkedinIntroSingleImage, {
  title: 'LinkedIn Ad',
  contentType: 'LinkedIn Ad',
  description: 'Introductory text',
  copy: 'Write an engrossing description for your LinkedIn Single Image Ad.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.linkedinSingleImageIntro,
  supportsCustomModel: true,
  channel: CHANNEL.linkedin,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsCallToAction: false,
  supportsBenchmark: true,
  supportsBackgroundUrlBriefExtraction: true,
  showDescriptionInList: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.linkedinHeadlineProduct, {
  title: 'LinkedIn Text Ad',
  contentType: 'LinkedIn Text Ad',
  description: 'Headline',
  copy: 'Create compelling headlines for your LinkedIn Text Ad.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.linkedinTextAdHeadline,
  supportsCustomModel: true,
  channel: CHANNEL.linkedin,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsCallToAction: false,
  supportsBenchmark: true,
  supportsBackgroundUrlBriefExtraction: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.linkedinIntroProduct, {
  title: 'LinkedIn Text Ad',
  contentType: 'LinkedIn Text Ad',
  description: 'Description',
  copy: 'Write engaging descriptions for your LinkedIn Text Ad.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.linkedinTextAdDescription,
  supportsCustomModel: true,
  channel: CHANNEL.linkedin,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsCallToAction: false,
  supportsBenchmark: true,
  supportsBackgroundUrlBriefExtraction: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.linkedinMessageAdSubject, {
  title: 'LinkedIn Message Ad',
  contentType: 'LinkedIn Message Ad',
  description: 'Subject',
  copy: 'Create compelling subject lines for your LinkedIn Message Ad.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.linkedinMessageAdSubject,
  supportsCustomModel: true,
  channel: CHANNEL.linkedin,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsCallToAction: false,
  supportsBenchmark: true,
  supportsBackgroundUrlBriefExtraction: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.linkedinMessageAdMessage, {
  title: 'LinkedIn Message Ad',
  contentType: 'LinkedIn Message Ad',
  description: 'Message',
  copy: 'Write engaging message content for your LinkedIn Message Ad.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.linkedinMessageAdMessage,
  supportsCustomModel: true,
  channel: CHANNEL.linkedin,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsCallToAction: false,
  supportsBenchmark: true,
  supportsBackgroundUrlBriefExtraction: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.linkedinMessageAdCta, {
  title: 'LinkedIn Message Ad',
  contentType: 'LinkedIn Message Ad',
  description: 'CTA',
  copy: 'Create compelling call-to-action for your LinkedIn Message Ad.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.linkedinMessageAdCta,
  supportsCustomModel: true,
  channel: CHANNEL.linkedin,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsCallToAction: false,
  supportsBenchmark: true,
  supportsBackgroundUrlBriefExtraction: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.linkedinConversationalAdOpeningMessage, {
  title: 'LinkedIn Conversation Ad',
  contentType: 'LinkedIn Conversation Ad',
  description: 'Opening Message',
  copy: 'Create engaging opening messages for your LinkedIn Conversation Ad.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.linkedinConversationalAdOpeningMessage,
  supportsCustomModel: true,
  channel: CHANNEL.linkedin,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsCallToAction: false,
  supportsBenchmark: true,
  supportsBackgroundUrlBriefExtraction: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.linkedinConversationalAdPrimaryCtaButtonText, {
  title: 'LinkedIn Conversation Ad',
  contentType: 'LinkedIn Conversation Ad',
  description: 'Primary CTA Button Text',
  copy: 'Create compelling primary CTA button text for your LinkedIn Conversation Ad.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.linkedinConversationalAdPrimaryCtaButtonText,
  supportsCustomModel: true,
  channel: CHANNEL.linkedin,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsCallToAction: false,
  supportsBenchmark: true,
  supportsBackgroundUrlBriefExtraction: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.linkedinConversationalAdFollowUpMessage, {
  title: 'LinkedIn Conversation Ad',
  contentType: 'LinkedIn Conversation Ad',
  description: 'Follow Up Message',
  copy: 'Write engaging follow-up messages for your LinkedIn Conversation Ad.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.linkedinConversationalAdFollowUpMessage,
  supportsCustomModel: true,
  channel: CHANNEL.linkedin,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsCallToAction: false,
  supportsBenchmark: true,
  supportsBackgroundUrlBriefExtraction: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.linkedinConversationalAdSecondaryCtaButtonText, {
  title: 'LinkedIn Conversation Ad',
  contentType: 'LinkedIn Conversation Ad',
  description: 'Secondary CTA Button Text',
  copy: 'Create compelling secondary CTA button text for your LinkedIn Conversation Ad.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.linkedinConversationalAdSecondaryCtaButtonText,
  supportsCustomModel: true,
  channel: CHANNEL.linkedin,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsCallToAction: false,
  supportsBenchmark: true,
  supportsBackgroundUrlBriefExtraction: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.linkedinLeadGenFormAdPrimaryText, {
  title: 'LinkedIn Lead Gen Form Ad',
  contentType: 'LinkedIn Lead Gen Form Ad',
  description: 'Primary text',
  copy: 'Write engaging primary text for your LinkedIn Lead Gen Form Ad.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.linkedinLeadGenFormAdPrimaryText,
  supportsCustomModel: true,
  channel: CHANNEL.linkedin,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsCallToAction: false,
  supportsBenchmark: true,
  supportsBackgroundUrlBriefExtraction: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.linkedinLeadGenFormAdHeadline, {
  title: 'LinkedIn Lead Gen Form Ad',
  contentType: 'LinkedIn Lead Gen Form Ad',
  description: 'Headline',
  copy: 'Create compelling headlines for your LinkedIn Lead Gen Form Ad.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.linkedinLeadGenFormAdHeadline,
  supportsCustomModel: true,
  channel: CHANNEL.linkedin,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsCallToAction: false,
  supportsBenchmark: true,
  supportsBackgroundUrlBriefExtraction: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.linkedinLeadGenFormAdOfferHeadline, {
  title: 'LinkedIn Lead Gen Form Ad',
  contentType: 'LinkedIn Lead Gen Form Ad',
  description: 'Offer Headline',
  copy: 'Create compelling offer headlines for your LinkedIn Lead Gen Form Ad.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.linkedinLeadGenFormAdOfferHeadline,
  supportsCustomModel: true,
  channel: CHANNEL.linkedin,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsCallToAction: false,
  supportsBenchmark: true,
  supportsBackgroundUrlBriefExtraction: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.linkedinLeadGenFormAdOfferDetails, {
  title: 'LinkedIn Lead Gen Form Ad',
  contentType: 'LinkedIn Lead Gen Form Ad',
  description: 'Offer Details',
  copy: 'Write engaging offer details for your LinkedIn Lead Gen Form Ad.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.linkedinLeadGenFormAdOfferDetails,
  supportsCustomModel: true,
  channel: CHANNEL.linkedin,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsCallToAction: false,
  supportsBenchmark: true,
  supportsBackgroundUrlBriefExtraction: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.articleBlogHeadline, {
  title: 'Blog',
  contentType: 'Blog',
  description: 'Headline',
  copy: "Give your content the world's best attention span",
  briefType: BRIEF_TYPE.canvas,
  defaultAssetType: ASSET_TYPE.freeTextHeadline,
  disableTOVModes: true,
  excludedFromSuggestedPersonas: true,
  supportsSectionStyle: true,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.productLaunchMultiPrompt, {
  title: 'Product Launch Campaign',
  contentType: 'Product Launch Campaign',
  copy: 'Stage a successful product launch with impactful ads, engaging posts, and a conversion-driven email.',
  multiPrompt: true,
  briefType: BRIEF_TYPE.unifiedProduct,
  supportsCustomerPersona: true,
  disableTOVModes: true,
  defaultAssetType: ASSET_TYPE.canvasFreestyle,
  supportsSuggestKeywords: true,
  supportsBackgroundUrlBriefExtraction: true,
  maxSuggestKeywords: 3,
  maxWordsPerKeyword: 4,
  defaultNumberOfVariations: 1,
  numberOfVariations: [1],
  promptFields: {
    templateOutputType: TEMPLATE_OUTPUT_TYPE.textOnly,
    titles: {
      product_details: 'Product details',
      benefit: 'Feature or benefit to focus on',
      hashtags: 'Hashtags',
      special_offer: 'Special offer',
    },
    placeholders: {
      product_details:
        'Enter your product name, as well as a detailed description of its features and benefits. ',
      benefit:
        'Describe a unique feature or benefit you want to focus on as part of this campaign.',
      hashtags: 'Enter up to 3 hashtags you would like to include in this campaign.',
      special_offer:
        'Enter the details of the offer or discount you want to include in this campaign. You can also include a promo code and availability dates.',
    },
    order: ['product_details', 'benefit', 'hashtags', 'special_offer'],
  },
  promptTools: {
    'Facebook Post': GENERATION_TOOL.facebookPost,
    'LinkedIn Post': GENERATION_TOOL.linkedinPost,
    'Instagram Post': GENERATION_TOOL.instagramCaption,
    'Facebook Ad': GENERATION_TOOL.facebookMixtureAd,
    'LinkedIn Ad': GENERATION_TOOL.linkedinMixtureAd,
    'Product Launch Email': GENERATION_TOOL.emailMixture,
  },
  longDesc:
    'Stage a successful product launch with impactful ads, engaging posts, and a conversion-driven email.',
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.landingPageProductAnnouncementMultiPrompt, {
  title: 'Product Landing Page',
  contentType: 'Product Landing Page',
  copy: "Turn your visitors into customers with a landing page that highlights your product's features and benefits.",
  multiPrompt: true,
  supportsBackgroundUrlBriefExtraction: true,
  briefType: BRIEF_TYPE.unifiedProduct,
  supportsCustomerPersona: true,
  disableTOVModes: true,
  defaultAssetType: ASSET_TYPE.canvasFreestyle,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 3,
  maxWordsPerKeyword: 4,
  defaultNumberOfVariations: 1,
  numberOfVariations: [1],
  promptFields: {
    templateOutputType: TEMPLATE_OUTPUT_TYPE.textOnly,
    titles: {
      product_details: 'Product details',
      benefit: 'Feature or benefit to highlight',
      call_to_action: 'Desired action',
    },
    placeholders: {
      product_details:
        'Enter your product name, as well as a detailed description of its features and benefits.',
      benefit: 'Describe a unique feature or benefit you want to highlight on your landing page.',
      call_to_action:
        'Enter the action you want your customer to take. For example, make an appointment, try a free demo, or download an article.',
    },
    order: ['product_details', 'benefit', 'call_to_action'],
  },
  promptTools: {
    Headline: GENERATION_TOOL.landingHeadline,
    Subheader: GENERATION_TOOL.landingSubheader,
    'Descriptive Paragraph': GENERATION_TOOL.freestyle,
    'Features Section Title': GENERATION_TOOL.landingSubheader,
    'Feature Descriptions': GENERATION_TOOL.landingFeatures,
    'Benefits Section Title': GENERATION_TOOL.landingSubheader,
    'Benefit Descriptions': GENERATION_TOOL.landingBenefits,
    CTA: GENERATION_TOOL.landingCTA,
  },
  longDesc:
    "Turn your visitors into customers with a landing page that highlights your product's features and benefits.",
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.emailDripMultiPrompt, {
  title: 'Welcome Email Drip Campaign',
  contentType: 'Welcome Email Drip Campaign',
  copy: 'Share information about your product with a series of welcome emails designed to convert new subscribers into paying customers.',
  multiPrompt: true,
  supportsBackgroundUrlBriefExtraction: true,
  briefType: BRIEF_TYPE.unifiedProduct,
  supportsCustomerPersona: true,
  disableTOVModes: true,
  defaultAssetType: ASSET_TYPE.canvasFreestyle,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 3,
  maxWordsPerKeyword: 4,
  defaultNumberOfVariations: 1,
  numberOfVariations: [1],
  promptFields: {
    templateOutputType: TEMPLATE_OUTPUT_TYPE.textOnly,
    titles: {
      brand_name: 'Brand name',
      product_details: 'Product details',
      feature: 'Feature to highlight',
      offer: 'Offer',
      customer_testimonial: 'Customer testimonial',
    },
    placeholders: {
      brand_name: 'Enter your brand name.',
      product_details:
        'Enter your product name, as well as a detailed description of its features and benefits.',
      feature: 'Describe a unique product feature you want to highlight as part of this campaign.',
      offer: 'Enter the details of the offer or discount you want to include in this campaign.',
      customer_testimonial:
        "Enter an explanation of how your product helped a customer. Include the customer's name and occupation.",
    },
    order: ['brand_name', 'product_details', 'feature', 'offer', 'customer_testimonial'],
  },
  promptTools: {
    'Email 1: Welcome Introduction': GENERATION_TOOL.emailMixture,
    'Email 2: Feature Spotlight': GENERATION_TOOL.emailMixture,
    'Email 3: Customer Testimonial': GENERATION_TOOL.emailMixture,
    'Email 4: Introductory Offer': GENERATION_TOOL.emailMixture,
  },
  longDesc:
    'Share information about your product with a series of welcome emails designed to convert new subscribers into paying customers.',
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.googleRSA, {
  title: 'Google Search Ad',
  contentType: 'Google Search Ad',
  titleForTooltip: 'Google Search Ad',
  mixtureSubGenerationTools: [
    GENERATION_TOOL.googleHeadlineProduct,
    GENERATION_TOOL.googleDescriptionProduct,
  ],
  mixtureSubGenerationToolsModeOverride: {
    [GENERATION_TOOL.googleHeadlineProduct]: AI_SUGGESTION_MODE.auto,
  },
  copy: 'Craft powerful headlines and descriptions that boost visibility and performance in Google Search.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.googleRSA,
  defaultNumberOfVariations: 1,
  numberOfVariations: [1],
  maxSuggestKeywords: 1,
  maxWordsPerKeyword: 5,
  channel: CHANNEL.google,
  maxCampaignKeywords: 3,
  supportsCallToAction: false,
  supportsPromptInstructions: true,
  supportsCustomerPersona: false,
  collapseAdvancedOptions: true,
  supportsBackgroundUrlBriefExtraction: true,
  supportsSubTemplates: true,
  supportsCampaignKeywords: true,
  supportsSuggestKeywords: false,
  disableTOVModes: true,
  supportsImage: false,
  supportsBenchmark: true,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.googlePerformanceMaxAd, {
  title: 'Google Performance Max Ad',
  contentType: 'Google Performance Max Ad',
  titleForTooltip: 'Google Performance Max Ad',
  mixtureSubGenerationTools: [
    GENERATION_TOOL.googleHeadlineProduct,
    GENERATION_TOOL.googleLongHeadlineProduct,
    GENERATION_TOOL.googleDescriptionProduct,
  ],
  mixtureSubGenerationToolsModeOverride: {
    [GENERATION_TOOL.googleHeadlineProduct]: AI_SUGGESTION_MODE.auto,
  },
  copy: 'Generate a complete set of high-impact creatives tailored for every PMax placement — effortlessly.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.googlePerformanceMaxAd,
  defaultNumberOfVariations: 1,
  numberOfVariations: [1],
  maxSuggestKeywords: 1,
  maxWordsPerKeyword: 4,
  channel: CHANNEL.google,
  maxCampaignKeywords: 3,
  supportsCallToAction: false,
  supportsPromptInstructions: true,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsBackgroundUrlBriefExtraction: true,
  supportsSubTemplates: true,
  supportsSuggestKeywords: false,
  disableTOVModes: true,
  supportsImage: false,
  supportsBenchmark: true,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.googleAppAd, {
  title: 'Google App Ad',
  contentType: 'Google App Ad',
  titleForTooltip: 'Google App Ad',
  mixtureSubGenerationTools: [
    GENERATION_TOOL.googleHeadlineProduct,
    GENERATION_TOOL.googleDescriptionProduct,
  ],
  mixtureSubGenerationToolsModeOverride: {
    [GENERATION_TOOL.googleHeadlineProduct]: AI_SUGGESTION_MODE.auto,
  },
  copy: 'Drive more installs and engagement with high-performing text and visual assets optimized for Google App Ads.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.googleAppAd,
  defaultNumberOfVariations: 1,
  numberOfVariations: [1],
  maxSuggestKeywords: 1,
  maxWordsPerKeyword: 5,
  channel: CHANNEL.google,
  maxCampaignKeywords: 3,
  supportsCallToAction: false,
  supportsPromptInstructions: true,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsBackgroundUrlBriefExtraction: true,
  supportsSuggestKeywords: false,
  disableTOVModes: true,
  supportsSubTemplates: true,
  supportsBenchmark: true,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.googleDemandGenAd, {
  title: 'Google Demand Gen Ad',
  contentType: 'Google Demand Gen Ad',
  titleForTooltip: 'Google Demand Gen Ad',
  mixtureSubGenerationTools: [
    GENERATION_TOOL.googleMediumHeadlineProduct,
    GENERATION_TOOL.googleLongHeadlineProduct,
    GENERATION_TOOL.googleDescriptionProduct,
  ],
  mixtureSubGenerationToolsModeOverride: {
    [GENERATION_TOOL.googleMediumHeadlineProduct]: AI_SUGGESTION_MODE.auto,
  },
  copy: 'Create eye-catching creatives that capture attention and drive action across YouTube, Discover, and Gmail.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.googleDemandGenAd,
  defaultNumberOfVariations: 1,
  numberOfVariations: [1],
  maxSuggestKeywords: 1,
  maxWordsPerKeyword: 5,
  channel: CHANNEL.google,
  maxCampaignKeywords: 3,
  supportsCallToAction: false,
  supportsPromptInstructions: true,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsBackgroundUrlBriefExtraction: true,
  supportsSubTemplates: true,
  supportsSuggestKeywords: false,
  disableTOVModes: true,
  supportsImage: false,
  supportsBenchmark: true,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.googleDisplayAd, {
  title: 'Google Display Ad',
  contentType: 'Google Display Ad',
  titleForTooltip: 'Google Display Ad',
  mixtureSubGenerationTools: [
    GENERATION_TOOL.googleHeadlineProduct,
    GENERATION_TOOL.googleLongHeadlineProduct,
    GENERATION_TOOL.googleDescriptionProduct,
  ],
  mixtureSubGenerationToolsModeOverride: {
    [GENERATION_TOOL.googleHeadlineProduct]: AI_SUGGESTION_MODE.auto,
  },
  copy: 'Produce compelling visuals and messages that stand out across the Google Display Network.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.googleDisplayAd,
  defaultNumberOfVariations: 1,
  numberOfVariations: [1],
  maxSuggestKeywords: 1,
  channel: CHANNEL.google,
  maxCampaignKeywords: 3,
  supportsCallToAction: false,
  supportsPromptInstructions: true,
  supportsSuggestKeywords: true,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsBackgroundUrlBriefExtraction: true,
  supportsSubTemplates: true,
  disableTOVModes: true,
  supportsImage: false,
  supportsBenchmark: true,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.googleShoppingAd, {
  title: 'Google Shopping Ad',
  contentType: 'Google Shopping Ad',
  titleForTooltip: 'Google Shopping Ad',
  mixtureSubGenerationTools: [
    GENERATION_TOOL.googleShoppingAdTitle,
    GENERATION_TOOL.googleShoppingAdDescription,
  ],
  copy: 'Write clear, high-converting product titles and descriptions for Google Shopping placements.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.googleShoppingAd,
  defaultNumberOfVariations: 1,
  maxSuggestKeywords: 1,
  maxWordsPerKeyword: 5,
  channel: CHANNEL.google,
  maxCampaignKeywords: 3,
  supportsCallToAction: false,
  supportsPromptInstructions: true,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsBackgroundUrlBriefExtraction: true,
  supportsSuggestKeywords: false,
  disableTOVModes: true,
  supportsSubTemplates: true,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.googleVideoAd, {
  title: 'Google Video Ad',
  contentType: 'Google Video Ad',
  titleForTooltip: 'Google Video Ad',
  mixtureSubGenerationTools: [
    GENERATION_TOOL.googleHeadlineProduct,
    GENERATION_TOOL.googleLongHeadlineProduct,
    GENERATION_TOOL.googleDescriptionProduct,
  ],
  mixtureSubGenerationToolsModeOverride: {
    [GENERATION_TOOL.googleHeadlineProduct]: AI_SUGGESTION_MODE.auto,
  },
  copy: 'Create engaging scripts and captions tailored for YouTube and Google video placements.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.googleVideoAd,
  defaultNumberOfVariations: 1,
  numberOfVariations: [1],
  maxSuggestKeywords: 1,
  channel: CHANNEL.google,
  maxCampaignKeywords: 3,
  supportsCallToAction: false,
  supportsPromptInstructions: true,
  supportsSuggestKeywords: true,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsBackgroundUrlBriefExtraction: true,
  supportsSubTemplates: true,
  disableTOVModes: true,
  supportsImage: false,
  supportsBenchmark: true,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.facebookMixtureAd, {
  title: 'Meta Image Ad',
  contentType: 'Meta Image Ad',
  titleForTooltip: 'Meta Image Ad',
  mixtureSubGenerationTools: [
    GENERATION_TOOL.facebookPrimaryTextProduct,
    GENERATION_TOOL.facebookHeadlineProduct,
  ],
  copy: 'Create highly engaging Meta (Facebook & Instagram) image-ad copy that drives clicks and conversions.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.facebookMixtureAd,
  defaultNumberOfVariations: 1,
  maxSuggestKeywords: 3,
  maxWordsPerKeyword: 4,
  channel: CHANNEL.facebook,
  maxCampaignKeywords: 3,
  supportsCallToAction: false,
  supportsPromptInstructions: true,
  supportsSuggestKeywords: true,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsBackgroundUrlBriefExtraction: true,
  supportsSubTemplates: true,
  supportsBenchmark: true,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.twitterMixtureAd, {
  title: 'X Image Ad',
  contentType: 'X Image Ad',
  titleForTooltip: 'X Image Ad',
  mixtureSubGenerationTools: [
    GENERATION_TOOL.twitterCopyProduct,
    GENERATION_TOOL.twitterTitleProduct,
  ],
  copy: 'Craft concise, engaging copy tailored for high-impact X (Twitter) Image Ads.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.twitterMixtureAd,
  defaultNumberOfVariations: 1,
  maxSuggestKeywords: 3,
  maxWordsPerKeyword: 4,
  maxCampaignKeywords: 3,
  supportsCallToAction: false,
  supportsPromptInstructions: true,
  supportsSuggestKeywords: true,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsBackgroundUrlBriefExtraction: true,
  supportsSubTemplates: true,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.pinterestMixtureAd, {
  title: 'Pinterest Image Ad',
  contentType: 'Pinterest Image Ad',
  titleForTooltip: 'Pinterest Image Ad',
  mixtureSubGenerationTools: [
    GENERATION_TOOL.pinterestTitleProduct,
    GENERATION_TOOL.pinterestDescriptionProduct,
  ],
  copy: 'Create visually aligned, keyword-optimized copy that inspires action on Pinterest.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.pinterestMixtureAd,
  defaultNumberOfVariations: 1,
  maxSuggestKeywords: 3,
  maxWordsPerKeyword: 4,
  maxCampaignKeywords: 3,
  supportsCallToAction: false,
  supportsPromptInstructions: true,
  supportsSuggestKeywords: true,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsBackgroundUrlBriefExtraction: true,
  supportsSubTemplates: true,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.linkedinMixtureAd, {
  title: 'LinkedIn Single Image Ad',
  contentType: 'LinkedIn Single Image Ad',
  titleForTooltip: 'LinkedIn Single Image Ad',
  mixtureSubAssetTypeSubtitles: {
    linkedin_single_image_headline: 'Headline',
    linkedin_single_image_intro: 'Introductory text',
  },
  mixtureSubGenerationTools: [
    GENERATION_TOOL.linkedinIntroSingleImage,
    GENERATION_TOOL.linkedinHeadlineSingleImage,
  ],
  copy: 'Generate strong, scroll-stopping copy optimized for LinkedIn Single Image Ads.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.linkedinMixtureAd,
  defaultNumberOfVariations: 1,
  maxSuggestKeywords: 3,
  maxWordsPerKeyword: 4,
  supportsCustomModel: true,
  channel: CHANNEL.linkedin,
  maxCampaignKeywords: 3,
  supportsCallToAction: false,
  supportsPromptInstructions: true,
  supportsSuggestKeywords: true,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsBackgroundUrlBriefExtraction: true,
  supportsSubTemplates: true,
  supportsBenchmark: true,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.linkedinDocumentAd, {
  title: 'LinkedIn Document Ad',
  contentType: 'LinkedIn Document Ad',
  titleForTooltip: 'LinkedIn Document Ad',
  mixtureSubAssetTypeSubtitles: {
    linkedin_single_image_intro: 'Introductory text',
    linkedin_single_image_headline: 'Headline',
  },
  mixtureSubGenerationTools: [
    GENERATION_TOOL.linkedinIntroSingleImage,
    GENERATION_TOOL.linkedinHeadlineSingleImage,
  ],
  copy: 'Create LinkedIn Document Ads that highlight key insights and encourage viewers to explore and download your content.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.linkedinDocumentAd,
  defaultNumberOfVariations: 1,
  maxSuggestKeywords: 3,
  maxWordsPerKeyword: 4,
  supportsCustomModel: true,
  channel: CHANNEL.linkedin,
  maxCampaignKeywords: 3,
  supportsCallToAction: false,
  supportsPromptInstructions: true,
  supportsSuggestKeywords: true,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsBackgroundUrlBriefExtraction: true,
  supportsSubTemplates: true,
  supportsBenchmark: true,
  supportsImage: false,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.linkedinVideoAd, {
  title: 'LinkedIn Video Ad',
  contentType: 'LinkedIn Video Ad',
  titleForTooltip: 'LinkedIn Video Ad',
  mixtureSubAssetTypeSubtitles: {
    linkedin_single_image_intro: 'Introductory text',
    linkedin_single_image_headline: 'Video headline',
  },
  mixtureSubGenerationTools: [
    GENERATION_TOOL.linkedinIntroSingleImage,
    GENERATION_TOOL.linkedinHeadlineSingleImage,
  ],
  copy: 'Create engaging LinkedIn Video Ads that hook viewers quickly and drive action within the feed.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.linkedinVideoAd,
  defaultNumberOfVariations: 1,
  maxSuggestKeywords: 3,
  maxWordsPerKeyword: 4,
  supportsCustomModel: true,
  channel: CHANNEL.linkedin,
  maxCampaignKeywords: 3,
  supportsCallToAction: false,
  supportsPromptInstructions: true,
  supportsSuggestKeywords: true,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsBackgroundUrlBriefExtraction: true,
  supportsSubTemplates: true,
  supportsBenchmark: true,
  supportsImage: false,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.linkedinTextAd, {
  title: 'LinkedIn Text Ad',
  contentType: 'LinkedIn Text Ad',
  titleForTooltip: 'LinkedIn Text Ad',
  mixtureSubAssetTypeSubtitles: {
    linkedin_text_ad_headline: 'Headline',
    linkedin_text_ad_description: 'Description',
  },
  mixtureSubGenerationTools: [
    GENERATION_TOOL.linkedinHeadlineProduct,
    GENERATION_TOOL.linkedinIntroProduct,
  ],
  copy: 'Craft concise LinkedIn Text Ads designed to capture attention and drive clicks with minimal copy.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.linkedinTextAd,
  defaultNumberOfVariations: 1,
  maxSuggestKeywords: 3,
  maxWordsPerKeyword: 4,
  supportsCustomModel: true,
  channel: CHANNEL.linkedin,
  maxCampaignKeywords: 3,
  supportsCallToAction: false,
  supportsPromptInstructions: true,
  supportsSuggestKeywords: true,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsBackgroundUrlBriefExtraction: true,
  supportsSubTemplates: true,
  supportsBenchmark: true,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.linkedinMessageAd, {
  title: 'LinkedIn Message Ad',
  contentType: 'LinkedIn Message Ad',
  titleForTooltip: 'LinkedIn Message Ad',
  mixtureSubAssetTypeSubtitles: {
    linkedin_message_ad_subject: 'Subject',
    linkedin_message_ad_message: 'Message',
    linkedin_message_ad_cta: 'CTA',
  },
  mixtureSubGenerationTools: [
    GENERATION_TOOL.linkedinMessageAdSubject,
    GENERATION_TOOL.linkedinMessageAdMessage,
    GENERATION_TOOL.linkedinMessageAdCta,
  ],
  copy: 'Create personalized LinkedIn Message Ads designed to drive replies, clicks, and conversions.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.linkedinMessageAd,
  defaultNumberOfVariations: 1,
  maxSuggestKeywords: 3,
  maxWordsPerKeyword: 4,
  supportsCustomModel: true,
  channel: CHANNEL.linkedin,
  maxCampaignKeywords: 3,
  supportsCallToAction: false,
  supportsPromptInstructions: true,
  supportsSuggestKeywords: true,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsBackgroundUrlBriefExtraction: true,
  supportsSubTemplates: true,
  supportsBenchmark: true,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.linkedinConversationAd, {
  title: 'LinkedIn Conversation Ad',
  contentType: 'LinkedIn Conversation Ad',
  titleForTooltip: 'LinkedIn Conversation Ad',
  mixtureSubAssetTypeSubtitles: {
    linkedin_conversational_ad_opening_message: 'Opening message',
    linkedin_conversational_ad_primary_cta_button_text: 'Primary CTA button',
    linkedin_conversational_ad_follow_up_message: 'Follow-up message',
    linkedin_conversational_ad_secondary_cta_button_text: 'Secondary CTA button',
  },
  mixtureSubGenerationTools: [
    GENERATION_TOOL.linkedinConversationalAdOpeningMessage,
    GENERATION_TOOL.linkedinConversationalAdPrimaryCtaButtonText,
    GENERATION_TOOL.linkedinConversationalAdFollowUpMessage,
    GENERATION_TOOL.linkedinConversationalAdSecondaryCtaButtonText,
  ],
  copy: 'Build interactive LinkedIn Conversation Ads that guide prospects through predefined message paths.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.linkedinConversationAd,
  defaultNumberOfVariations: 1,
  maxSuggestKeywords: 3,
  maxWordsPerKeyword: 4,
  supportsCustomModel: true,
  channel: CHANNEL.linkedin,
  maxCampaignKeywords: 3,
  supportsCallToAction: false,
  supportsPromptInstructions: true,
  supportsSuggestKeywords: true,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsBackgroundUrlBriefExtraction: true,
  supportsSubTemplates: true,
  supportsBenchmark: true,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.linkedinLeadGenFormAd, {
  title: 'LinkedIn Lead Gen Form Ad',
  contentType: 'LinkedIn Lead Gen Form Ad',
  titleForTooltip: 'LinkedIn Lead Gen Form Ad',
  mixtureSubAssetTypeSubtitles: {
    linkedin_lead_gen_form_ad_primary_text: 'Primary text',
    linkedin_lead_gen_form_ad_headline: 'Headline',
    linkedin_lead_gen_form_ad_offer_headline: 'Offer headline',
    linkedin_lead_gen_form_ad_offer_details: 'Offer details',
  },
  mixtureSubGenerationTools: [
    GENERATION_TOOL.linkedinLeadGenFormAdPrimaryText,
    GENERATION_TOOL.linkedinLeadGenFormAdHeadline,
    GENERATION_TOOL.linkedinLeadGenFormAdOfferHeadline,
    GENERATION_TOOL.linkedinLeadGenFormAdOfferDetails,
  ],
  copy: 'Generate LinkedIn Lead Gen Form Ads that encourage sign-ups while keeping the experience friction-free.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.linkedinLeadGenFormAd,
  defaultNumberOfVariations: 1,
  maxSuggestKeywords: 3,
  maxWordsPerKeyword: 4,
  supportsCustomModel: true,
  channel: CHANNEL.linkedin,
  maxCampaignKeywords: 3,
  supportsCallToAction: false,
  supportsPromptInstructions: true,
  supportsSuggestKeywords: true,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsBackgroundUrlBriefExtraction: true,
  supportsSubTemplates: true,
  supportsBenchmark: true,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.outbrainAd, {
  title: 'Outbrain Ad',
  contentType: 'Outbrain Ad',
  titleForTooltip: 'Outbrain Ad',
  mixtureSubGenerationTools: [
    GENERATION_TOOL.outbrainHeadlineProduct,
    GENERATION_TOOL.outbrainDescriptionProduct,
  ],
  copy: 'Craft compelling headlines and descriptions that boost discovery and clicks across Outbrain.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.outbrainAd,
  defaultNumberOfVariations: 1,
  maxSuggestKeywords: 3,
  maxWordsPerKeyword: 4,
  channel: CHANNEL.outbrain,
  maxCampaignKeywords: 3,
  supportsCallToAction: false,
  supportsPromptInstructions: true,
  supportsSuggestKeywords: true,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsBackgroundUrlBriefExtraction: true,
  supportsSubTemplates: true,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.taboolaAd, {
  title: 'Taboola Ad',
  contentType: 'Taboola Ad',
  titleForTooltip: 'Taboola Ad',
  mixtureSubGenerationTools: [
    GENERATION_TOOL.taboolaHeadlineProduct,
    GENERATION_TOOL.taboolaDescriptionProduct,
  ],
  mixtureSubAssetTypeSubtitles: {
    outbrain_headline: 'Title', // We need to override headline since BE is hardcoded to use outbrain_headline
  },
  copy: 'Generate attention-grabbing titles and descriptions optimized for Taboola’s native network.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.taboolaAd,
  defaultNumberOfVariations: 1,
  maxSuggestKeywords: 3,
  maxWordsPerKeyword: 4,
  channel: CHANNEL.taboola,
  maxCampaignKeywords: 3,
  supportsCallToAction: false,
  supportsPromptInstructions: true,
  supportsSuggestKeywords: true,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsBackgroundUrlBriefExtraction: true,
  supportsSubTemplates: true,
});

/**
 * "landing" data-driven tools
 */
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.landingHeadline, {
  title: 'Webpage Headline',
  description: 'Headline',
  titleForTooltip: 'Webpage Headline',
  contentType: 'Webpage',
  copy: 'Generate the perfect headline for your landing page.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.landingBuilderHeadline,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  maxWordsPerKeyword: 4,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsPromptInstructions: true,
  supportsBackgroundUrlBriefExtraction: true,
  supportsSectionStyle: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.landingSubheader, {
  title: 'Webpage Subheadline',
  description: 'Subheadline',
  titleForTooltip: 'Webpage Subheadline',
  contentType: 'Webpage',
  copy: 'Write compelling subheaders that hook readers in.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.landingBuilderSubHeadline,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsPromptInstructions: true,
  supportsBackgroundUrlBriefExtraction: true,
  supportsSectionStyle: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.landingCTA, {
  title: 'Webpage Call to Action',
  contentType: 'Webpage',
  description: 'Call to Action',
  copy: 'Create irresistible call to actions that actually convert.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.landingBuilderCta,
  collapseAdvancedOptions: true,
  supportsPromptInstructions: true,
  supportsBackgroundUrlBriefExtraction: true,
  supportsSectionStyle: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.landingBenefits, {
  title: 'Webpage Benefits',
  contentType: 'Webpage',
  description: 'Benefits',
  copy: "Tell readers what they'll gain by using your product.",
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.benefits,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 3,
  maxWordsPerKeyword: 4,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsPromptInstructions: true,
  supportsBackgroundUrlBriefExtraction: true,
  supportsSectionHeader: true,
  supportsSectionStyle: true,
  isPlural: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.landingFeatures, {
  title: 'Webpage Features',
  contentType: 'Webpage',
  description: 'Features',
  copy: "Build out a list of your product's key features.",
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.features,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 3,
  maxWordsPerKeyword: 4,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsPromptInstructions: true,
  supportsBackgroundUrlBriefExtraction: true,
  supportsSectionHeader: true,
  supportsSectionStyle: true,
  isPlural: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.landingSEOMetadata, {
  title: 'SEO Meta Description',
  contentType: 'Website',
  description: 'SEO Meta Description',
  copy: 'Generate click-worthy meta descriptions to increase your visibility on Google.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.landingSEOMetadata,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
  maxWordsPerKeyword: 4,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsPromptInstructions: true,
  supportsBackgroundUrlBriefExtraction: true,
  supportsCampaignKeywords: true,
  maxCampaignKeywords: 2,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.landingSEOMetaTitle, {
  title: 'SEO Meta Title',
  contentType: 'Website',
  description: 'SEO Meta Title',
  copy: 'Generate click-worthy meta titles to increase your visibility on Google.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.landingSEOMetaTitle,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.landingSectionHeadline, {
  title: 'Webpage Header',
  contentType: 'Webpage',
  description: 'Header',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.landingSectionHeadline,
  supportsSectionStyle: true,
});

/**
 * "product listing" data-driven tools
 */
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.amazonProductListing, {
  title: 'Amazon Listing',
  contentType: 'Amazon Listing',
  description: 'Text',
  copy: 'Write a persuasive paragraph for your Amazon listings.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.amazonProductListing,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 3,
  maxWordsPerKeyword: 4,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsPromptInstructions: true,
  supportsBackgroundUrlBriefExtraction: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.walmartProductListing, {
  title: 'Walmart Listing',
  contentType: 'Walmart Listing',
  description: 'Text',
  copy: 'Write a persuasive paragraph for your Walmart listings.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.walmartProductListing,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 3,
  maxWordsPerKeyword: 4,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsPromptInstructions: true,
  supportsBackgroundUrlBriefExtraction: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.generalProductListing, {
  title: 'General Listing',
  contentType: 'General Listing',
  copy: 'Write a persuasive paragraph for your General listings.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.generalProductListing,
  basicHasMultipleGenerationModes: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 3,
  maxWordsPerKeyword: 4,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsPromptInstructions: true,
  supportsBackgroundUrlBriefExtraction: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.basicPersonalBio, {
  title: 'Personal Bio',
  contentType: 'Personal Bio',
  copy: 'Generate a Personal Biography that stands out.',
  briefType: BRIEF_TYPE.canvas,
  defaultAssetType: ASSET_TYPE.personalBio,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 3,
  maxWordsPerKeyword: 4,
  supportsCustomerPersona: true,
  excludedFromSuggestedPersonas: true,
  disableTOVModes: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.basicCompanyBio, {
  title: 'Company Bio',
  contentType: 'Company Bio',
  copy: 'Generate an "about us" section for your company.',
  briefType: BRIEF_TYPE.canvas,
  defaultAssetType: ASSET_TYPE.companyBio,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 3,
  maxWordsPerKeyword: 4,
  supportsCustomerPersona: true,
  excludedFromSuggestedPersonas: true,
  disableTOVModes: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.basicEngagingQuestions, {
  title: 'Engaging Questions',
  contentType: 'Engaging Questions',
  copy: 'Create engaging questions for any topic.',
  briefType: BRIEF_TYPE.canvas,
  defaultAssetType: ASSET_TYPE.engagingQuestions,
  supportsCustomerPersona: true,
  excludedFromSuggestedPersonas: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.basicQuoraAnswers, {
  title: 'Quora Answers',
  contentType: 'Quora Answers',
  copy: 'Generate knowledgable responses to quora questions.',
  briefType: BRIEF_TYPE.canvas,
  defaultAssetType: ASSET_TYPE.quoraAnswers,
  supportsCustomerPersona: true,
  excludedFromSuggestedPersonas: true,
});

/**
 * deprecated "product listing" data-driven tools
 */
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.amazonProductDescription, {
  title: 'Amazon Listing',
  contentType: 'Amazon Listing',
  description: 'Paragraph Description',
  copy: 'Write a persuasive paragraph for your Amazon listings.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.amazonProductDescription,
  maxWordsPerKeyword: 4,
  supportsCustomerPersona: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.amazonFeatureList, {
  title: 'Amazon Listing',
  contentType: 'Amazon Listing',
  description: 'Feature List',
  copy: 'Write a persuasive feature list for your Amazon listings.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.amazonFeatureList,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 3,
  maxWordsPerKeyword: 4,
  supportsCustomerPersona: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.walmartProductDescription, {
  title: 'Walmart Listing',
  contentType: 'Walmart Listing',
  description: 'Paragraph Description',
  copy: 'Write a persuasive paragraph for your Walmart listings.',
  defaultAssetType: ASSET_TYPE.walmartProductDescription,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 3,
  briefType: BRIEF_TYPE.unifiedProduct,
  maxWordsPerKeyword: 4,
  supportsCustomerPersona: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.walmartFeatureList, {
  title: 'Walmart Listing',
  contentType: 'Walmart Listing',
  description: 'Feature List',
  copy: 'Write a persuasive paragraph for your Walmart listings.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.walmartFeatureList,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 3,
  maxWordsPerKeyword: 4,
  supportsCustomerPersona: true,
});

/**
 * "Social" data-driven tools
 */
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.instagramCaption, {
  title: 'Instagram Post',
  contentType: 'Instagram Post',
  description: 'Text',
  copy: 'Generate catchy captions for your Instagram posts.',
  briefType: BRIEF_TYPE.socialManual,
  defaultAssetType: ASSET_TYPE.instagramCaption,
  maxSuggestKeywords: 3,
  maxWordsPerKeyword: 4,
  supportsCustomerPersona: true,
  supportsPromptInstructions: true,
  supportsBackgroundUrlBriefExtraction: true,
  collapseAdvancedOptions: true,
  supportsSuggestKeywords: true,
  supportsBenchmark: true,
  supportsCustomModel: true,
  channel: CHANNEL.instagram,
  supportsSubTemplates: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.facebookPost, {
  title: 'Facebook Post',
  contentType: 'Facebook Post',
  description: 'Text',
  copy: 'Create engaging copy for your Facebook posts to maximize organic reach.',
  briefType: BRIEF_TYPE.socialManual,
  defaultAssetType: ASSET_TYPE.facebookPost,
  maxSuggestKeywords: 3,
  maxWordsPerKeyword: 4,
  supportsCustomerPersona: true,
  supportsPromptInstructions: true,
  supportsBackgroundUrlBriefExtraction: true,
  collapseAdvancedOptions: true,
  supportsSuggestKeywords: true,
  supportsBenchmark: true,
  supportsCustomModel: true,
  channel: CHANNEL.facebookPages,
  supportsSubTemplates: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.tweet, {
  title: 'X Post',
  contentType: 'X Post',
  description: 'Text',
  copy: 'Generate witty tweets to keep your followers engaged.',
  briefType: BRIEF_TYPE.socialManual,
  defaultAssetType: ASSET_TYPE.tweet,
  supportsCustomerPersona: true,
  supportsPromptInstructions: true,
  supportsBackgroundUrlBriefExtraction: true,
  collapseAdvancedOptions: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 3,
  maxWordsPerKeyword: 4,
  supportsCustomModel: true,
  supportsBenchmark: true,
  channel: CHANNEL.twitterSocial,
  supportsSubTemplates: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.linkedinPost, {
  title: 'LinkedIn Post',
  contentType: 'LinkedIn Post',
  description: 'Text',
  copy: 'Cut through the noise by generating compelling copy for your LinkedIn posts.',
  briefType: BRIEF_TYPE.socialManual,
  defaultAssetType: ASSET_TYPE.linkedinPost,
  supportsCustomerPersona: true,
  supportsPromptInstructions: true,
  supportsBackgroundUrlBriefExtraction: true,
  collapseAdvancedOptions: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 3,
  maxWordsPerKeyword: 4,
  supportsBenchmark: true,
  supportsCustomModel: true,
  channel: CHANNEL.linkedinSocial,
  supportsSubTemplates: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.pinterestMixturePost, {
  title: 'Pinterest Pin',
  contentType: 'Pinterest Pin',
  description: 'Text',
  titleForTooltip: 'Pinterest Pin',
  mixtureSubGenerationTools: [
    GENERATION_TOOL.pinterestPostTitle,
    GENERATION_TOOL.pinterestPostDescription,
  ],
  copy: 'Take it one step further with dazzling Pinterest Ads.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.pinterestMixturePost,
  defaultNumberOfVariations: 1,
  maxSuggestKeywords: 3,
  maxWordsPerKeyword: 4,
  maxCampaignKeywords: 3,
  supportsCallToAction: false,
  supportsPromptInstructions: true,
  supportsSuggestKeywords: true,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsBackgroundUrlBriefExtraction: true,
  supportsSubTemplates: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.pinterestPostDescription, {
  title: 'Pinterest Pin',
  contentType: 'Pinterest Pin',
  description: 'Description',
  copy: 'Take it one step further with dazzling Pinterest descriptions.',
  briefType: BRIEF_TYPE.socialManual,
  defaultAssetType: ASSET_TYPE.pinterestPost,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsPromptInstructions: true,
  supportsBackgroundUrlBriefExtraction: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
  maxWordsPerKeyword: 4,
  showDescriptionInList: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.pinterestPostTitle, {
  title: 'Pinterest Pin',
  contentType: 'Pinterest Pin',
  description: 'Title',
  copy: 'Step up your Pinterest game with stellar titles.',
  briefType: BRIEF_TYPE.socialManual,
  defaultAssetType: ASSET_TYPE.pinterestPostTitle,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsPromptInstructions: true,
  supportsBackgroundUrlBriefExtraction: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  maxWordsPerKeyword: 4,
  showDescriptionInList: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.youtubeMixturePost, {
  title: 'YouTube Video Title & Description ',
  contentType: 'YouTube Video Title & Description ',
  titleForTooltip: 'YouTube Video Title & Description ',
  mixtureSubGenerationTools: [GENERATION_TOOL.youtubeTitle, GENERATION_TOOL.youtubeDescription],
  copy: 'Write smart YouTube titles to help viewers find your videos.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.youtubeMixturePost,
  maxSuggestKeywords: 3,
  maxWordsPerKeyword: 4,
  maxCampaignKeywords: 3,
  supportsCallToAction: false,
  supportsPromptInstructions: true,
  supportsSuggestKeywords: true,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsBackgroundUrlBriefExtraction: true,
  supportsSubTemplates: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.youtubeDescription, {
  title: 'Youtube Video',
  contentType: 'Youtube Video',
  description: 'Description',
  copy: "Generate enticing YouTube descriptions that grab viewers' attention.",
  briefType: BRIEF_TYPE.socialManual,
  defaultAssetType: ASSET_TYPE.youtubeDescription,
  supportsCustomerPersona: true,
  supportsPromptInstructions: true,
  collapseAdvancedOptions: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 3,
  maxWordsPerKeyword: 4,
  showDescriptionInList: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.youtubeTitle, {
  title: 'Youtube Video',
  contentType: 'Youtube Video',
  description: 'Title',
  copy: 'Write smart YouTube titles to help viewers find your videos.',
  briefType: BRIEF_TYPE.socialManual,
  defaultAssetType: ASSET_TYPE.youtubeTitle,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsSuggestKeywords: true,
  supportsPromptInstructions: true,
  maxSuggestKeywords: 3,
  maxWordsPerKeyword: 4,
  showDescriptionInList: true,
});
/**
 * "content" data-driven tools
 */
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.facebookPrimaryTextContent, {
  title: 'Facebook Article Promotion',
  contentType: 'Facebook Article Promotion',
  description: 'Primary Text',
  copy: 'Generate exceptionally effective copy for your promoted content ads on Facebook.',
  briefType: BRIEF_TYPE.unifiedContent,
  defaultAssetType: ASSET_TYPE.facebookPrimaryText,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.facebookHeadlineContent, {
  title: 'Facebook Article Promotion',
  contentType: 'Facebook Article Promotion',
  description: 'Headline',
  copy: 'Generate ad headlines that promote your content.',
  briefType: BRIEF_TYPE.unifiedContent,
  defaultAssetType: ASSET_TYPE.facebookHeadline,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.outbrainHeadlineContent, {
  title: 'Outbrain Article Promotion',
  contentType: 'Outbrain Article Promotion',
  description: 'Headline',
  copy: 'Drive more traffic to your content with irresistible Outbrain headlines.',
  briefType: BRIEF_TYPE.unifiedContent,
  defaultAssetType: ASSET_TYPE.outbrainHeadline,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.taboolaHeadlineContent, {
  title: 'Taboola Article Promotion',
  contentType: 'Taboola Article Promotion',
  description: 'Headline',
  copy: 'Drive more traffic to your content with irresistible Taboola headlines.',
  briefType: BRIEF_TYPE.unifiedContent,
  defaultAssetType: ASSET_TYPE.taboolaHeadline,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.twitterCopyContent, {
  title: 'X Article Promotion',
  contentType: 'X Article Promotion',
  description: 'Copy',
  copy: 'Write scroll-stopping copy for your promoted content ads on X.',
  briefType: BRIEF_TYPE.unifiedContent,
  defaultAssetType: ASSET_TYPE.twitterCopy,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 3,
  maxWordsPerKeyword: 4,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.linkedinIntroContent, {
  title: 'LinkedIn Content Text Ad',
  contentType: 'LinkedIn Content Text Ad',
  description: 'Copy',
  briefType: BRIEF_TYPE.unifiedContent,
  defaultAssetType: ASSET_TYPE.linkedinIntro,
  collapseAdvancedOptions: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.linkedinHeadlineContent, {
  title: 'LinkedIn Content Text Ad',
  contentType: 'LinkedIn Content Text Ad',
  description: 'Headline',
  briefType: BRIEF_TYPE.unifiedContent,
  defaultAssetType: ASSET_TYPE.linkedinTitle,
  collapseAdvancedOptions: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.pinterestDescriptionContent, {
  title: 'Pinterest Article Promotion',
  contentType: 'Pinterest Article Promotion',
  description: 'Description',
  copy: 'Write a punchy Pinterest Ad that gets people to your content.',
  briefType: BRIEF_TYPE.unifiedContent,
  defaultAssetType: ASSET_TYPE.pinterestDescription,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 3,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.pinterestTitleContent, {
  title: 'Pinterest Article Promotion',
  contentType: 'Pinterest Article Promotion',
  description: 'Title',
  copy: 'Create stellar titles that will make your content shine.',
  briefType: BRIEF_TYPE.unifiedContent,
  defaultAssetType: ASSET_TYPE.pinterestTitle,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
});

/**
 * "email" and "sms" data-driven tools
 */
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.emailSubject, {
  title: 'Email Subject Line',
  contentType: 'Email',
  description: 'Subject Line',
  copy: 'Write enticing subject lines for all types of emails.',
  briefType: BRIEF_TYPE.unifiedPromotionalEmail,
  defaultAssetType: ASSET_TYPE.emailSubject,
  supportsSuggestKeywords: true,
  supportsCustomModel: true,
  channel: CHANNEL.emailChannel,
  maxSuggestKeywords: 1,
  maxWordsPerKeyword: 3,
  supportsCustomerPersona: true,
  supportsPromptInstructions: true,
  collapseAdvancedOptions: true,
  supportsBackgroundUrlBriefExtraction: true,
  supportsBenchmark: true,
  variationLabelWithoutDescription: true,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.emailBody, {
  title: 'Email Body',
  contentType: 'Email',
  copy: 'Create captivating email bodies suitable for any purpose, ensuring engagement, clarity, and effective communication.',
  description: 'Body',
  briefType: BRIEF_TYPE.unifiedPromotionalEmail,
  defaultAssetType: ASSET_TYPE.emailBody,
  supportsSuggestKeywords: true,
  supportsCustomModel: true,
  channel: CHANNEL.emailChannel,
  maxSuggestKeywords: 1,
  maxWordsPerKeyword: 3,
  supportsCustomerPersona: true,
  supportsPromptInstructions: true,
  collapseAdvancedOptions: true,
  supportsBackgroundUrlBriefExtraction: true,
  supportsBenchmark: true,
  variationLabelWithoutDescription: true,
  noScore: true,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.emailMixture, {
  title: 'Email',
  contentType: 'Email',
  titleForTooltip: 'Email',
  mixtureSubAssetTypeSubtitles: {
    email_subject: 'Subject Line',
    email_body: 'Body',
    email_promotional_body: 'Promotional Body',
    email_cold_outreach_body: 'Cold Body',
  },
  mixtureSubGenerationTools: [GENERATION_TOOL.emailSubject, GENERATION_TOOL.emailBody],
  classifiedMixtureSubGenerationTools: [
    GENERATION_TOOL.emailPromotionalBody,
    GENERATION_TOOL.emailColdOutreachBody,
  ],
  copy: 'Generate polished, on-brand email copy from a simple prompt.',
  briefType: BRIEF_TYPE.unifiedPromotionalEmail,
  defaultAssetType: ASSET_TYPE.emailMixture,
  supportsPromptInstructions: true,
  supportsSuggestKeywords: true,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsBackgroundUrlBriefExtraction: true,
  supportsCustomModel: true,
  channel: CHANNEL.emailChannel,
  maxSuggestKeywords: 1,
  maxWordsPerKeyword: 3,
  supportsBenchmark: true,
  variationLabelWithoutDescription: true,
  supportsSubTemplates: true,
  defaultNumberOfVariations: 1,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.smsPromotional, {
  title: 'SMS',
  contentType: 'SMS',
  description: 'Text',
  copy: 'Generate text messages that actually sell.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.sms,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 3,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsBackgroundUrlBriefExtraction: true,
  supportsPromptInstructions: true,
});

/**
 * Rewrite tools
 */
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.emailBodyRewrite, {
  title: 'Email Body',
  contentType: 'Email',
  description: 'Body',
  copy: 'Say goodbye to dull emails. Rewrite your email copy to make it even more persuasive.',
  briefType: BRIEF_TYPE.email,
  defaultAssetType: ASSET_TYPE.emailBody,
  generatingLoaderText: 'Email Body',
  supportsSuggestKeywords: true,
  supportsBenchmark: true,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.emailSubjectLineRewrite, {
  title: 'Subject Line',
  contentType: 'Email',
  description: 'Subject Line',
  copy: 'Boost open rates by sprucing up your existing email subject line.',
  briefType: BRIEF_TYPE.email,
  defaultAssetType: ASSET_TYPE.emailSubjectLine,
  generatingLoaderText: 'Email subject line',
  supportsSuggestKeywords: true,
  supportsBenchmark: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.facebookHeadlineProductRewrite, {
  title: 'Meta Ad',
  contentType: 'Meta Ad',
  copy: 'Rewrite your existing Meta Ad copy to make it more effective.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.facebookHeadline,
  disableFormula: true,
  generatingLoaderText: 'Meta ad - headline',
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 3,
  supportsBenchmark: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.facebookPrimaryTextProductRewrite, {
  title: 'Meta Ad',
  contentType: 'Meta Ad',
  copy: 'Rewrite your existing Meta Ad copy to make it more effective.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.facebookPrimaryText,
  generatingLoaderText: 'Meta ad - primary text',
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 3,
  supportsBenchmark: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.googleHeadlineProductRewrite, {
  title: 'Google Search Ad',
  contentType: 'Google Search Ad',
  copy: 'Make your Google Ads Headlines pop with a copy refresh.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.googleHeadline,
  supportsCampaignKeywords: true,
  maxWordsPerKeyword: 5,
  maxCampaignKeywords: 3,
  generatingLoaderText: 'Google search ad - headline',
  supportsBenchmark: true,
  disableTOVModes: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.googleDescriptionProductRewrite, {
  title: 'Google Search Ad',
  contentType: 'Google Search Ad',
  copy: 'Make your Google Ads Descriptions pop with a copy refresh.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.googleDescription,
  maxCampaignKeywords: 3,
  supportsCampaignKeywords: true,
  maxWordsPerKeyword: 5,
  generatingLoaderText: 'Google search ad - description',
  supportsBenchmark: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.outbrainHeadlineProductRewrite, {
  title: 'Outbrain Ad',
  contentType: 'Outbrain Ad',
  description: 'Headline',
  copy: 'Draw in more traffic by refreshing your Outbrain headlines.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.outbrainHeadline,
  generatingLoaderText: 'Outbrain headline',
  supportsSuggestKeywords: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.taboolaHeadlineProductRewrite, {
  title: 'Taboola Ad',
  contentType: 'Taboola Ad',
  copy: 'Boost your conversion rate by improving your Taboola headlines.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.taboolaHeadline,
  generatingLoaderText: 'Taboola - title',
  supportsSuggestKeywords: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.twitterCopyProductRewrite, {
  title: 'X Ad',
  contentType: 'X Ad',
  copy: 'Spruce up your X ads and make them stand out in the feed.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.twitterCopy,
  generatingLoaderText: 'X ad - copy',
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 3,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.pinterestDescriptionProductRewrite, {
  title: 'Pinterest Ad',
  contentType: 'Pinterest Ad',
  copy: 'Transform you existing Pinterest ads into high-converting ones.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.pinterestDescription,
  generatingLoaderText: 'Pinterest ad - description',
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 3,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.landingHeadlineRewrite, {
  title: 'Webpage Header',
  contentType: 'Webpage',
  description: 'Headline',
  copy: 'Revamp your landing page headline to make it even more powerful and catchy.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.landingBuilderHeadline,
  generatingLoaderText: 'Webpage headline',
  supportsSuggestKeywords: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.landingSubheaderRewrite, {
  title: 'Webpage Subheadline',
  contentType: 'Webpage',
  description: 'Subheadline',
  copy: "Rewrite your landing page subheaders to help capture readers' attention.",
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.landingBuilderSubHeadline,
  generatingLoaderText: 'Webpage subheader',
  supportsSuggestKeywords: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.landingSEOMetadataRewrite, {
  title: 'SEO Meta Description',
  contentType: 'Website',
  copy: "Battling Google's algorithm? Rewrite your SEO meta describes to boost visibility.",
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.landingSEOMetadata,
  generatingLoaderText: 'SEO meta description',
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.emailColdOutreachBodyRewrite, {
  title: 'Cold Email',
  contentType: 'Cold Email',
  copy: 'Say goodbye to dull emails. Rewrite your email copy to make it even more persuasive.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.emailColdOutreachBody,
  generatingLoaderText: 'Cold email',
  supportsSuggestKeywords: true,
  supportsBenchmark: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.emailPromotionalBodyRewrite, {
  title: 'Promotional Email',
  contentType: 'Promotional Email',
  copy: 'Say goodbye to dull emails. Rewrite your email copy to make it even more persuasive.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.emailPromotionalBody,
  generatingLoaderText: 'Promotional email',
  supportsSuggestKeywords: true,
  supportsBenchmark: true,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.smsPromotionalRewrite, {
  title: 'Promotional SMS',
  contentType: 'Promotional SMS',
  copy: 'Trying to increase sales? Rewrite your Promotion SMS to draw in customers.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.sms,
  generatingLoaderText: 'Promotional SMS',
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 3,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.linkedinHeadlineSingleImageRewrite, {
  title: 'LinkedIn Single Image Ad',
  contentType: 'LinkedIn Single Image Ad',
  copy: 'Get more clicks with an improved headline for your LinkedIn Single Image Ad.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.linkedinSingleImageHeadline,
  generatingLoaderText: 'LinkedIn single image ad - headline',
  supportsSuggestKeywords: true,
  supportsBenchmark: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.linkedinIntroSingleImageRewrite, {
  title: 'LinkedIn Single Image Ad',
  contentType: 'LinkedIn Single Image Ad',
  copy: 'Get better engagement with an improved description for your LinkedIn Single Image Ad.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.linkedinSingleImageIntro,
  generatingLoaderText: 'LinkedIn single image ad - copy',
  supportsSuggestKeywords: true,
  supportsBenchmark: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.instagramCaptionRewrite, {
  title: 'Instagram Post',
  contentType: 'Instagram Post',
  description: 'Text',
  copy: 'Boost your likes by kicking your Instagram caption up a notch.',
  briefType: BRIEF_TYPE.socialManual,
  defaultAssetType: ASSET_TYPE.instagramCaption,
  generatingLoaderText: 'Instagram caption',
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 3,
  supportsBenchmark: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.facebookPostRewrite, {
  title: 'Facebook Post',
  contentType: 'Facebook Post',
  description: 'Text',
  copy: 'Bring your Facebook post text to the next level.',
  briefType: BRIEF_TYPE.socialManual,
  defaultAssetType: ASSET_TYPE.facebookPost,
  generatingLoaderText: 'Facebook post',
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 3,
  supportsBenchmark: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.pinterestPinTitleRewrite, {
  title: 'Pinterest Pin',
  contentType: 'Pinterest Pin',
  copy: 'Spruce up your Pinterest pin with a new and improved description.',
  generatingLoaderText: 'Pinterest pin - title',
  briefType: BRIEF_TYPE.socialManual,
  defaultAssetType: ASSET_TYPE.pinterestPostTitle,
  supportsSuggestKeywords: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.pinterestPinDescriptionRewrite, {
  title: 'Pinterest Pin',
  contentType: 'Pinterest Pin',
  copy: 'Give your Pinterest pin a revised title that really pops.',
  briefType: BRIEF_TYPE.socialManual,
  defaultAssetType: ASSET_TYPE.pinterestPost,
  generatingLoaderText: 'Pinterest pin - description',
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.tweetRewrite, {
  title: 'X Post',
  contentType: 'X Post',
  copy: 'Make the most out of your tweets with a copy tune-up.',
  briefType: BRIEF_TYPE.socialManual,
  defaultAssetType: ASSET_TYPE.tweet,
  generatingLoaderText: 'X post',
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 3,
  supportsBenchmark: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.linkedinPostRewrite, {
  title: 'LinkedIn Post',
  contentType: 'LinkedIn Post',
  copy: 'Bring your LinkedIn post text to the next level.',
  briefType: BRIEF_TYPE.socialManual,
  defaultAssetType: ASSET_TYPE.linkedinPost,
  generatingLoaderText: 'LinkedIn post',
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 3,
  supportsBenchmark: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.emailSubjectRewrite, {
  title: 'Subject Line',
  contentType: 'Email',
  copy: 'Boost open rates by sprucing up your existing email subject line.',
  briefType: BRIEF_TYPE.email,
  defaultAssetType: ASSET_TYPE.emailSubject,
  generatingLoaderText: 'Email subject line',
  supportsSuggestKeywords: true,
  supportsBenchmark: true,
});

/**
 * "Teads" data-driven tools
 */
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.teadsSingleVideoHeadline, {
  title: 'Teads Ad',
  contentType: 'Teads Ad',
  description: 'Headline',
  copy: 'Generate effective headlines for ads.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.teadsSingleVideoHeadline,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
  maxWordsPerKeyword: 4,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsCallToAction: false,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.teadsSingleVideoDescription, {
  title: 'Teads Ad',
  contentType: 'Teads Ad',
  description: 'Description',
  copy: 'Generate effective descriptions for ads.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.teadsSingleVideoDescription,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
  maxWordsPerKeyword: 4,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsCallToAction: false,
});
/**
 * Deprecated email tools
 */

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.emailSubjectLine, {
  title: 'Subject Line',
  contentType: 'Email',
  description: '',
  copy: 'Write enticing subject lines for all types of emails.',
  briefType: BRIEF_TYPE.email,
  defaultAssetType: ASSET_TYPE.emailSubjectLine,
  supportsSuggestKeywords: true,
  supportsCustomModel: true,
  channel: CHANNEL.emailChannel,
  maxSuggestKeywords: 1,
  maxWordsPerKeyword: 3,
  supportsCustomerPersona: true,
  excludedFromSuggestedPersonas: true,
  collapseAdvancedOptions: true,
  supportsBenchmark: true,
  supportsSectionStyle: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.emailPromotionalSubjectLine, {
  title: 'Email Promotional',
  contentType: 'Email',
  description: 'Subject Line',
  copy: 'Write one of these brilliant email subject lines for an exciting promotion.',
  briefType: BRIEF_TYPE.unifiedPromotionalEmail,
  defaultAssetType: ASSET_TYPE.emailPromotionalSubjectLine,
  supportsSuggestKeywords: true,
  supportsCustomModel: true,
  channel: CHANNEL.emailChannel,
  maxSuggestKeywords: 1,
  maxWordsPerKeyword: 4,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsBenchmark: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.emailPromotionalBody, {
  title: 'Email Promotional',
  contentType: 'Email',
  description: 'Promotional Body',
  copy: 'Have something amazing for your customers? Let them know! Write an awesome email about a new product or an exciting promotion.',
  briefType: BRIEF_TYPE.unifiedPromotionalEmail,
  defaultAssetType: ASSET_TYPE.emailPromotionalBody,
  supportsSuggestKeywords: true,
  supportsCustomModel: true,
  channel: CHANNEL.emailChannel,
  maxSuggestKeywords: 1,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsBenchmark: true,
  variationLabelWithoutDescription: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.emailColdOutreachSubjectLine, {
  title: 'Email Cold',
  contentType: 'Email',
  description: 'Subject Line',
  copy: 'Generate subject lines that get people to open emails.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.emailColdOutreachSubjectLine,
  supportsCustomModel: true,
  channel: CHANNEL.emailChannel,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsBenchmark: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.emailColdOutreachBody, {
  title: 'Email Cold',
  contentType: 'Email',
  description: 'Cold Body',
  copy: 'Create powerful, convincing emails to attract potential customers to your product or service.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.emailColdOutreachBody,
  supportsSuggestKeywords: true,
  supportsCustomModel: true,
  channel: CHANNEL.emailChannel,
  maxSuggestKeywords: 3,
  maxWordsPerKeyword: 4,
  supportsCustomerPersona: true,
  collapseAdvancedOptions: true,
  supportsBenchmark: true,
  variationLabelWithoutDescription: true,
});

/**
 * deprecated "Teads" data-driven tools
 */
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.teadsSingleImageHeadline, {
  title: 'Teads Single Image',
  contentType: 'Teads Single Image',
  description: 'Headline',
  copy: 'Generate effective descriptions and headlines for Single Image ads.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.teadsSingleImageHeadline,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
  maxWordsPerKeyword: 4,
  supportsCustomerPersona: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.teadsSingleImageDescription, {
  title: 'Teads Single Image',
  contentType: 'Teads Single Image',
  description: 'Description',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.teadsSingleImageDescription,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
  maxWordsPerKeyword: 4,
  supportsCustomerPersona: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.teadsCarouselHeadline, {
  title: 'Teads Carousel',
  contentType: 'Teads Carousel',
  copy: 'Generate effective headlines for Carousel ads.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.teadsCarouselHeadline,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
  maxWordsPerKeyword: 4,
  supportsCustomerPersona: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.teadsSocialLayout1Headline, {
  title: 'Teads Social Layout I',
  contentType: 'Teads Social Layout I',
  description: 'Headline',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.teadsSocialLayout1Headline,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  maxWordsPerKeyword: 4,
  supportsCustomerPersona: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.teadsSocialLayout1PrimaryText, {
  title: 'Teads Social Layout I',
  contentType: 'Teads Social Layout I',
  description: 'Primary Text',
  copy: 'Generate effective primary texts, headlines and descriptions for Social Layout I ads.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.teadsSocialLayout1PrimaryText,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
  maxWordsPerKeyword: 4,
  supportsCustomerPersona: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.teadsSocialLayout1Description, {
  title: 'Teads Social Layout I',
  contentType: 'Teads Social Layout I',
  description: 'Description',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.teadsSocialLayout1Description,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 1,
  maxWordsPerKeyword: 4,
  supportsCustomerPersona: true,
});
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.teadsSocialLayout2PrimaryText, {
  title: 'Teads Social Layout II',
  contentType: 'Teads Social Layout II',
  description: 'Primary Text',
  copy: 'Generate effective primary texts for Social Layout II ads.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.teadsSocialLayout2PrimaryText,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 2,
  maxWordsPerKeyword: 4,
  supportsCustomerPersona: true,
});

/**
 * "Create your own" freestyle tools
 */
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.freestyle, {
  title: 'Prompt',
  contentType: 'Prompt',
  description: '',
  copy: 'Generate any copy you want!\nTell the AI what to write and how.',
  briefType: BRIEF_TYPE.canvas,
  defaultAssetType: ASSET_TYPE.canvasFreestyle,
  defaultNumberOfVariations: 1,
  supportsBackgroundUrlBriefExtraction: true,
  supportsCustomerPersona: true,
  supportsPromptInstructions: true,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 3,
  maxWordsPerKeyword: 4,
  supportsSectionHeader: true,
  supportsSectionStyle: true,
  supportsImage: true,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.createYourOwn, {
  title: 'Prompt',
  contentType: 'Prompt',
  description: '',
  copy: 'Generate any copy you want!\nTell the AI what to write and how.',
  briefType: BRIEF_TYPE.canvas,
  defaultAssetType: ASSET_TYPE.canvasFreestyle,
  defaultNumberOfVariations: 1,
  supportsBackgroundUrlBriefExtraction: true,
});

/**
 * LPCO tools
 */
GENERATION_TOOL_PROPS.add(GENERATION_TOOL.lpcoLandingHeadline, {
  title: '',
  contentType: '',
  description: '',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.landingHeadline,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.lpcoLandingCTA, {
  title: 'Create A New Use Case',
  contentType: 'Create A New Use Case',
  description: '',
  copy: 'Generate content for any purpose and any use case in a breeze.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.landingCTA,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.lpcoLandingProductDescription, {
  title: '',
  contentType: '',
  description: '',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.landingProductDescription,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.lpcoLandingProductName, {
  title: '',
  contentType: '',
  description: '',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.landingProductName,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.lpcoLandingCopy, {
  title: '',
  contentType: '',
  description: '',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.landingCopy,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.wtmDefault, {
  title: 'Website automation',
  contentType: 'Website automation',
  copy: 'Optimize on-site copy to display the right message to the right audience.',
  briefType: BRIEF_TYPE.unifiedProduct,
  defaultAssetType: ASSET_TYPE.landingProductDescription,
});

/**
 * Blog tools
 */

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.blogDescription, {
  title: 'Blog post',
  contentType: 'Blog post',
  copy: 'Go from a blank page to a brilliant blog post in minutes.',
  channel: CHANNEL.blogUrls,
  supportsCustomModel: true,
  briefType: BRIEF_TYPE.blogPostBuilder,
  defaultAssetType: ASSET_TYPE.blogPostContent,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.blogPostSectionHeadline, {
  title: 'Blog Header',
  contentType: 'Blog',
  briefType: BRIEF_TYPE.blogPostBuilder,
  defaultAssetType: ASSET_TYPE.blogPostSectionHeadline,
  supportsSectionStyle: true,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.blogPostSectionSubHeadline, {
  title: 'Blog Subheadline',
  contentType: 'Blog',
  briefType: BRIEF_TYPE.blogPostBuilder,
  defaultAssetType: ASSET_TYPE.blogPostSectionSubHeadline,
  supportsSectionStyle: true,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.blogPostIntroParagraph, {
  title: 'Blog Introduction',
  contentType: 'Blog',
  briefType: BRIEF_TYPE.blogPostBuilder,
  defaultAssetType: ASSET_TYPE.introParagraph,
  supportsSectionHeader: true,
  supportsSectionStyle: true,
});

/**
 * Long form tools
 */

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.longFormGeneral, {
  title: 'Geneal long form',
  contentType: 'Geneal long form',
  briefType: BRIEF_TYPE.canvas,
  defaultAssetType: ASSET_TYPE.longFormSectionHeadline,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.longFormBlank, {
  title: 'Blank',
  contentType: 'Blank',
  copy: 'Create compelling long-form content on any topic to inform, educate, or engage your audience.',
  briefType: BRIEF_TYPE.canvas,
  defaultAssetType: ASSET_TYPE.longFormSectionHeadline,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.longFormContentImprover, {
  title: 'Long form content improver',
  contentType: 'Long form content improver',
  briefType: BRIEF_TYPE.canvas,
  defaultAssetType: ASSET_TYPE.longFormSectionHeadline,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.emailHeadline, {
  title: 'Email Headline',
  contentType: 'Email',
  briefType: BRIEF_TYPE.canvas,
  defaultAssetType: ASSET_TYPE.emailHeadline,
  supportsSectionStyle: true,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.emailCta, {
  title: 'Email Call to Action',
  contentType: 'Email',
  briefType: BRIEF_TYPE.canvas,
  defaultAssetType: ASSET_TYPE.emailCta,
  supportsSectionStyle: true,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.emailIntroParagraph, {
  title: 'Email Introduction',
  contentType: 'Email',
  briefType: BRIEF_TYPE.canvas,
  defaultAssetType: ASSET_TYPE.emailIntroParagraph,
  supportsSectionHeader: true,
  supportsSectionStyle: true,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.emailSectionHeadline, {
  title: 'Email Header',
  contentType: 'Email',
  briefType: BRIEF_TYPE.canvas,
  defaultAssetType: ASSET_TYPE.emailSectionHeadline,
  supportsSectionStyle: true,
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.customMultiPrompt, {
  title: 'Saved template',
  contentType: 'Saved template',
  copy: 'Custom tool description',
  multiPrompt: true,
  supportsBackgroundUrlBriefExtraction: true,
  briefType: BRIEF_TYPE.unifiedProduct,
  supportsCustomerPersona: true,
  disableTOVModes: true,
  defaultAssetType: ASSET_TYPE.canvasFreestyle,
  supportsSuggestKeywords: true,
  maxSuggestKeywords: 3,
  maxWordsPerKeyword: 4,
  defaultNumberOfVariations: 1,
  promptFields: {
    templateOutputType: TEMPLATE_OUTPUT_TYPE.textOnly,
    titles: {},
    placeholders: {},
    order: [],
  },
  numberOfVariations: [1],
  promptTools: {},
  longDesc: 'Custom tool long description',
});

GENERATION_TOOL_PROPS.add(GENERATION_TOOL.chromeExtensionExtract, {
  title: 'Extracted Content',
  contentType: 'Extracted Content',
  titleForTooltip: 'Content extracted from page',
  defaultAssetType: ASSET_TYPE.canvasFreestyle,
  briefType: BRIEF_TYPE.unifiedProduct,
});

const getToolProp = <T>(
  type: GenerationTool,
  propName: keyof GenerationToolProps,
  defaultValue?: T
): T | undefined => {
  const props = GENERATION_TOOL_PROPS.get(type);
  if (props && props[propName] != null) {
    return props[propName] as T;
  }
  return defaultValue;
};

/* Tool texts/copy properties */
export const getToolTitle = (tool: GenerationTool) => getToolProp<string>(tool, 'title', '');
export const getToolContentType = (tool: GenerationTool) =>
  getToolProp<string>(tool, 'contentType', '');
export const getToolTitleForTooltip = (tool: GenerationTool) =>
  getToolProp<string>(tool, 'titleForTooltip', getToolTitle(tool));
export const getToolDescription = (tool: GenerationTool) =>
  getToolProp<string>(tool, 'description', '');
export const getToolGeneratingLoaderText = (tool: GenerationTool) =>
  getToolProp<string>(tool, 'generatingLoaderText', '');
export const getToolCopy = (tool: GenerationTool) => getToolProp<string>(tool, 'copy', '');
export const getToolLongDesc = (tool: GenerationTool) => getToolProp<string>(tool, 'longDesc', '');

/* Tool feature support flags */

export const doesToolSupportSuggestKeywords = (tool: GenerationTool) =>
  getToolProp<boolean>(tool, 'supportsSuggestKeywords', false);

export const doesToolSupportPromptInstruction = (tool: GenerationTool) =>
  getToolProp<boolean>(tool, 'supportsPromptInstructions', false);

export const doesToolSupportCampaignKeywords = (tool: GenerationTool) =>
  getToolProp<boolean>(tool, 'supportsCampaignKeywords', false);

export const doesToolSupportCustomerPersona = (tool: GenerationTool) =>
  getToolProp<boolean>(tool, 'supportsCustomerPersona', false);

export const doesToolSupportsSubTemplates = (tool: GenerationTool) =>
  getToolProp<boolean>(tool, 'supportsSubTemplates', false);

export const doesToolSupportCustomModel = (tool: GenerationTool) =>
  getToolProp<boolean>(tool, 'supportsCustomModel', false);

export const doesToolSupportBenchmark = (tool: GenerationTool) =>
  getToolProp<boolean>(tool, 'supportsBenchmark', false);

export const doesToolSupportImage = (tool: GenerationTool) =>
  getToolProp<boolean>(tool, 'supportsImage', true);

export const doesToolSupportCallToAction = (tool: GenerationTool) =>
  getToolProp<boolean>(tool, 'supportsCallToAction', false);

export const doesToolSupportBackgroundUrlBriefExtraction = (tool: GenerationTool) =>
  getToolProp<boolean>(tool, 'supportsBackgroundUrlBriefExtraction', false);

export const doesToolSupportSectionHeader = (tool: GenerationTool) =>
  getToolProp<boolean>(tool, 'supportsSectionHeader', false);

export const doesToolSupportSectionStyle = (tool: GenerationTool) =>
  getToolProp<boolean>(tool, 'supportsSectionStyle', false);

export const doesToolSupportCsvExport = (tool: GenerationTool) =>
  getToolProp<boolean>(tool, 'supportsCsvExport', false);

export const doesToolHasMultipleGenerationModes = (tool: GenerationTool) =>
  getToolProp<boolean>(tool, 'basicHasMultipleGenerationModes', false);

export const shouldDisableTOVModes = (tool: GenerationTool) =>
  getToolProp<boolean>(tool, 'disableTOVModes', false);

export const shouldDisableBrandVocabulary = (tool: GenerationTool) =>
  getToolProp<boolean>(tool, 'disableBrandVocabulary', false);

export const getToolMaxSuggestKeywords = (tool: GenerationTool) =>
  getToolProp<number>(tool, 'maxSuggestKeywords', 1);

export const getToolMaxCampaignKeywords = (tool: GenerationTool) =>
  getToolProp<number>(tool, 'maxCampaignKeywords', 1);

export const getToolMaxWordsPerKeyword = (tool: GenerationTool) =>
  getToolProp<number>(tool, 'maxWordsPerKeyword', 4);

export const getToolDefaultNumberOfVariations = (tool: GenerationTool) =>
  getToolProp<number>(tool, 'defaultNumberOfVariations');

export const getToolNumberOfVariations = (tool: GenerationTool) =>
  getToolProp<number[]>(tool, 'numberOfVariations');

export const getToolBriefType = (tool: GenerationTool) => getToolProp<BriefType>(tool, 'briefType');

export const getToolDefaultAssetType = (tool: GenerationTool) =>
  getToolProp<AssetType>(tool, 'defaultAssetType');
export const isToolVariationLabelWithoutDescription = (tool: GenerationTool) =>
  getToolProp<boolean>(tool, 'variationLabelWithoutDescription', false);

export const doesToolSupportCollapseAdvancedOptions = (tool: GenerationTool) =>
  getToolProp<boolean>(tool, 'collapseAdvancedOptions', false);

export const isToolExcludedFromSuggestedPersonas = (tool: GenerationTool) =>
  getToolProp<boolean>(tool, 'excludedFromSuggestedPersonas', false);

export const getToolMixtureSubAssetTypeSubtitles = (
  tool: GenerationTool,
  subAssetType: AssetType
): string => {
  const subToolsCount = getToolMixtureSubGenerationTools(tool).length;
  if (subToolsCount === 0) {
    return '';
  }

  const subtitles = getToolProp<Record<AssetType, string>>(tool, 'mixtureSubAssetTypeSubtitles');
  return subtitles?.[subAssetType] || getAssetLabel(subAssetType) || '';
};
export const getToolMixtureSubGenerationTools = (tool: GenerationTool): GenerationTool[] => {
  return getToolProp<GenerationTool[]>(tool, 'mixtureSubGenerationTools') || [];
};
export const getToolClassifiedMixtureSubGenerationTools = (
  tool: GenerationTool
): GenerationTool[] => {
  return getToolProp<GenerationTool[]>(tool, 'classifiedMixtureSubGenerationTools') || [];
};
export const getToolMixtureSubGenerationToolsModeOverride = (tool: GenerationTool) => {
  return getToolProp<Record<GenerationTool, AiSuggestionMode>>(
    tool,
    'mixtureSubGenerationToolsModeOverride'
  );
};
export const isMixtureTool = (tool: GenerationTool) => {
  const subTools = getToolMixtureSubGenerationTools(tool);
  return subTools ? subTools.length > 0 : false;
};

export const isMultiPromptTool = (tool: GenerationTool) =>
  getToolProp<boolean>(tool, 'multiPrompt', false);
export const getMultiPromptFields = (tool: GenerationTool) =>
  getToolProp<PromptFields>(tool, 'promptFields');
export const getMultiPromptSubtools = (tool: GenerationTool) =>
  getToolProp<PromptTools>(tool, 'promptTools', {});

export const isNoScoreTool = (tool: GenerationTool) => getToolProp<boolean>(tool, 'noScore', false);
export const isPluralTool = (tool: GenerationTool) => getToolProp<boolean>(tool, 'isPlural', false);
export const isHiddenTool = (tool: GenerationTool) => getToolProp<boolean>(tool, 'hidden', false);

export const getToolChannel = (tool: GenerationTool) => getToolProp<Channel>(tool, 'channel');
export const getBackendResourcesFromTool = (tool: GenerationTool): BackendChannel[] => {
  const toolChannel = getToolChannel(tool);
  if (!toolChannel) {
    return [];
  }

  if (toolChannel === CHANNEL.emailChannel) {
    return EMAILS_CHANNELS_AND_RESOURCES.map((channel) => alignChannelWithBackend(channel));
  }

  return [alignChannelWithBackend(toolChannel)];
};

export const doesToolSupportFormulas = (tool: GenerationTool) =>
  (doesToolHasMultipleGenerationModes(tool) || isDataDrivenGenerationTool(tool)) &&
  !isMultiPromptTool(tool) &&
  isTextGenerationTool(tool) &&
  tool !== GENERATION_TOOL.googleRSA &&
  tool !== GENERATION_TOOL.googlePerformanceMaxAd;

/**
 * Get full generation tool title
 * @param tool - generation tool
 * @param [options] - options
 * @param [options.description] - tool description override
 * @returns - tool title
 */
export const getToolLabel = (tool: GenerationTool): string => {
  const assetType = getToolDefaultAssetType(tool);
  const title = getToolContentType(tool) + (assetType ? ` - ${getAssetLabel(assetType)}` : '');
  return title;
};

export const shouldShowDescriptionInList = (tool: GenerationTool) =>
  getToolProp<boolean>(tool, 'showDescriptionInList', false);
